import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import html2canvas from 'html2canvas';
import Field from "./components/Field";
import { getOrGeneratePresets } from "./utils/presets";
import { processRoute } from "./utils/routeProcessing";
import { getDynamicBallSegments } from "./utils/ballLogic";
import { currentVersion, changelog } from "./utils/version";
import "./index.css";

// New UI Imports
import HeaderBar from './ui/HeaderBar';
import QuickRoutesPanel from './ui/LeftRailLibrary';
import LeftRailBuilder from './ui/LeftRailBuilder';
import QuickRoutesView from './ui/LibraryView';
import BuilderPane from './ui/BuilderPane';
import InputModal from './ui/InputModal';
import ShareModal from './ui/ShareModal';
import PrintModal from './ui/PrintModal';
import NewPlayModal from './ui/NewPlayModal';
import RosterModal from './ui/RosterModal';
import RoutePreview from './ui/RoutePreview';
import VideoPanel from './ui/VideoPanel';

// Profile & Firebase Imports
import { db, auth, storage } from './firebase';
import { increment, writeBatch, doc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PROFILE_CONFIG, SPEED_PROFILE_40S, normalizeProfile, getProfileConfig } from './data/profileConfig';

// Context & Hooks
import { useAuth } from './context/AuthContext';
import { usePlays } from './hooks/usePlays';
import { usePlaybooks } from './hooks/usePlaybooks';
import { useRoster } from './hooks/useRoster';


import {
  OFFENSE_FORMATIONS_6v6,
  OFFENSE_FORMATIONS_5v5,
  DEFENSE_FORMATIONS_6v6,
  DEFENSE_FORMATIONS_5v5,
  FORMATIONS,
  FORMATIONS_BY_FORMAT
} from './data/formations';
const INITIAL_PLAYERS = [...OFFENSE_FORMATIONS_6v6['I-Formation']];
const initialBallSpec = {
  enabled: false, startPos: null, snapToPlayerId: null, snapTime: 0.5,
  throwLevel: 'hs', throwType: 'pass', segments: [], currentHolderId: null,
};

const toSeconds = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
};

const cleanFileName = (name) => name.replace(/[^a-zA-Z0-9._-]+/g, '_');
const VIDEO_LIBRARY_HASHES = new Set(['#video-library', '#videos']);

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const getVideoPanelWidth = (video) => {
  const size = Math.max(0.85, Math.min(4, Number(video?.size || 1)));
  return Math.round(360 * size);
};

const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const makeVideoId = () => `video-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const hashText = (value) => {
  const text = String(value || '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const normalizeVideoAttachment = (video, index = 0) => {
  if (!video) return null;
  const id = video.id || `video-${hashText(video.storagePath || video.url || video.fileName || index)}`;
  const fallbackName = video.fileName || `Video ${index + 1}`;
  return {
    ...video,
    id,
    type: video.type || 'upload',
    label: video.label || fallbackName,
    fileName: video.fileName || fallbackName,
    playStartAt: toSeconds(video.playStartAt),
    trimStart: toSeconds(video.trimStart),
    trimEnd: toSeconds(video.trimEnd),
    snapAt: toSeconds(video.snapAt ?? video.startAt ?? video.trimStart),
    formationAt: toSeconds(video.formationAt ?? video.trimStart),
    muted: true,
    size: Number(video.size || 1),
    note: video.note || '',
    positives: video.positives || video.whatWentRight || '',
    negatives: video.negatives || video.whatWentWrong || '',
    audioNoteUrl: video.audioNoteUrl || '',
    audioNoteStoragePath: video.audioNoteStoragePath || ''
  };
};

const normalizeVideoCollection = (source = {}) => {
  const rawVideos = Array.isArray(source.videos || source.playVideos)
    ? (source.videos || source.playVideos)
    : (source.video ? [source.video] : []);
  const videos = rawVideos.map(normalizeVideoAttachment).filter(Boolean);
  const requestedActiveId = source.activeVideoId || source.video?.id;
  const activeVideoId = videos.some(video => video.id === requestedActiveId)
    ? requestedActiveId
    : (videos[0]?.id || '');
  return { videos, activeVideoId };
};

const getActiveVideo = (videos, activeVideoId) => (
  videos.find(video => video.id === activeVideoId) || videos[0] || null
);

const normalizeLibraryVideo = (video, index = 0) => {
  if (!video) return null;
  const id = video.id || `library-${hashText(video.storagePath || video.url || video.fileName || index)}`;
  const fallbackName = video.fileName || `Video ${index + 1}`;
  return {
    ...video,
    id,
    label: video.label || fallbackName,
    fileName: video.fileName || fallbackName,
    type: video.type || 'upload',
    muted: true
  };
};

const normalizeVideoLibrary = (items) => (
  (Array.isArray(items) ? items : []).map(normalizeLibraryVideo).filter(Boolean)
);

const createVideoAttachment = (libraryVideo, index = 0) => ({
  id: makeVideoId(),
  libraryVideoId: libraryVideo.id,
  type: libraryVideo.type || 'upload',
  url: libraryVideo.url,
  storagePath: libraryVideo.storagePath,
  fileName: libraryVideo.fileName,
  label: libraryVideo.label || libraryVideo.fileName || `Video ${index + 1}`,
  contentType: libraryVideo.contentType || '',
  playStartAt: 0,
  trimStart: 0,
  trimEnd: 0,
  snapAt: 0,
  formationAt: 0,
  muted: true,
  size: 1,
  note: '',
  positives: '',
  negatives: '',
  audioNoteUrl: '',
  audioNoteStoragePath: '',
  attachedAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

const PlayerMenu = ({ target, routes, onSelectRoute, onDeleteRoute, onFreeDraw, onClose, onApplyZone, onClonePlayer, onSetMotion, style, isLocked, profileConfig }) => {
  const [expandedCat, setExpandedCat] = useState(() => {
    if (!target || !target.label) return null;
    const l = target.label.toUpperCase();
    if (l.includes('QB')) return 'QB (dropbacks / rollouts)';
    if (l.includes('WR') || l.includes('TE')) return 'WR / TE Routes';
    if (l.includes('RB') || l.includes('HB') || l === 'F' || l === 'T') return 'RB Routes'; // Tailback/Fullback
    if (['C', 'G', 'T', 'OL', 'RT', 'LT', 'RG', 'LG'].some(x => l === x)) return 'Blocking';
    return null; // Default closed
  });
  const allPresets = React.useMemo(() => getOrGeneratePresets(target, profileConfig), [target, profileConfig]);

  // "Add Option" Helper
  const handleAddOption = () => {
    // Find primary route end
    const lastRoute = routes.filter(r => r.playerId === target.id && r.type !== 'option').pop();
    // Actually, usually one primary route. 
    // If no route, maybe start at player?
    const startPos = lastRoute?.pointsAbs ? lastRoute.pointsAbs[lastRoute.pointsAbs.length - 1] : { x: target.x, y: target.y };
    onFreeDraw('option', startPos);
  };

  return (
    <div className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden text-sm flex flex-col z-50" style={{ ...style, width: '280px', maxHeight: '500px' }} onClick={(e) => e.stopPropagation()}>
      <div className="bg-gray-800 px-3 py-2 border-b border-gray-700 flex justify-between items-center"><span className="font-bold text-gray-100">Player Menu</span><button onClick={onClose} className="text-gray-400 hover:text-white px-1">×</button></div>
      <div className="p-2 grid grid-cols-2 gap-2 border-b border-gray-700 bg-gray-900">
        <button
          disabled={isLocked}
          onClick={() => !isLocked && onFreeDraw()}
          className={`px-3 py-1.5 rounded text-xs font-bold flex items-center justify-center gap-1 ${isLocked ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
        >
          <span className="text-lg">🎯</span> Start Free Draw
        </button>
        <button
          disabled={isLocked}
          onClick={() => !isLocked && onDeleteRoute()}
          className={`px-3 py-1.5 rounded text-xs font-bold ${isLocked ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700' : 'bg-red-700 hover:bg-red-600 text-white'}`}
        >
          Delete Route
        </button>

        {routes.some(r => r.playerId === target.id) && (
          <button
            disabled={isLocked}
            onClick={() => {
              const r = routes.find(ri => ri.playerId === target.id);
              if (r) onSetMotion(r.id);
            }}
            className={`col-span-2 px-3 py-2 rounded text-xs font-black flex items-center justify-center gap-2 border-2 ${isLocked ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-indigo-900/60 hover:bg-indigo-800 border-indigo-500/50 text-indigo-100 uppercase tracking-tighter shadow-lg'}`}
          >
            <span className="text-base">📍</span> Set Pre-Snap Motion Point
          </button>
        )}
        {routes.some(r => r.playerId === target.id) && (
          <button
            disabled={isLocked}
            onClick={() => !isLocked && handleAddOption()}
            className={`col-span-2 px-3 py-2 rounded text-xs font-black flex items-center justify-center gap-2 border-2 ${isLocked ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-amber-900/60 hover:bg-amber-800 border-amber-500/50 text-amber-100 uppercase tracking-tighter shadow-lg'}`}
          >
            <span className="text-base">🔀</span> Add Option Route
          </button>
        )}
      </div>
      <div className="overflow-y-auto flex-1 bg-gray-900">
        {Object.entries(allPresets).map(([catName, catRoutes]) => {
          const isExpanded = expandedCat === catName;
          const routeKeys = catRoutes ? Object.keys(catRoutes) : [];
          if (routeKeys.length === 0) return null;
          return (
            <div key={catName} className="border-b border-gray-800">
              <button
                className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-800 transition-colors ${isExpanded ? 'bg-gray-800 text-white' : 'text-gray-300'}`}
                onClick={() => setExpandedCat(isExpanded ? null : catName)}
              >
                <span className="text-[10px]">{isExpanded ? '▼' : '▶'}</span>
                <span className="font-medium text-xs">{catName}</span>
              </button>
              {isExpanded && (
                <div className="grid grid-cols-2 gap-1 p-2 bg-gray-900">
                  {routeKeys.map(rName => {
                    const item = catRoutes[rName];
                    // Check if it's a zone preset (object) or route factory (function)
                    const isZone = typeof item === 'object' && item !== null && !Array.isArray(item);

                    return (
                      <button
                        key={rName}
                        disabled={isLocked}
                        className={`text-left px-2 py-1.5 border rounded text-xs truncate transition-colors ${isLocked ? 'bg-gray-900 text-gray-600 border-gray-800 cursor-not-allowed' : 'bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300'}`}
                        title={rName.replace(/_/g, ' ')}
                        onClick={() => {
                          if (isLocked) return;
                          if (isZone) {
                            onApplyZone?.(item);
                          } else {
                            onSelectRoute(item, catName);
                          }
                        }}
                      >
                        {rName.replace(/_/g, ' ')}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-2 border-t border-gray-800 bg-gray-900 grid grid-cols-2 gap-2">
        <button
          disabled={isLocked}
          onClick={() => {
            if (isLocked) return;
            onClonePlayer?.(target.id);
          }}
          className={`border px-3 py-1.5 rounded text-xs font-bold flex items-center justify-center gap-1 ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed' : 'bg-gray-800 hover:bg-gray-700 text-blue-400 border-gray-700'}`}
        >
          <span>👥</span> Clone
        </button>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 border border-gray-700 rounded bg-gray-800">
          Close
        </button>
      </div>
    </div>
  );
};
const describeBallChain = (ballSpec, players) => {
  if (!ballSpec || !ballSpec.enabled) return 'No ball logic.';
  const nameFor = (id) => players.find(p => p.id === id)?.label || `#${id}`;
  const parts = [];
  const snapSeg = ballSpec.segments?.find(s => s.kind === 'snap');
  if (snapSeg) { parts.push(`Center → ${nameFor(snapSeg.toPlayerId)}`); }
  const handoffs = (ballSpec.segments || []).filter(s => s.kind === 'handoff');
  handoffs.forEach(h => { parts.push(`${nameFor(h.fromPlayerId)} → ${nameFor(h.toPlayerId)} (handoff)`); });
  const throwSeg = ballSpec.segments?.find(s => s.kind === 'throw');
  if (throwSeg) { parts.push(`${nameFor(throwSeg.fromPlayerId)} → ${nameFor(throwSeg.toPlayerId)} (throw)`); }
  return parts.length ? parts.join('  |  ') : 'No ball exchanges defined.';
};

const FieldContextMenu = ({ x, y, pos, selectedCount, onAction, onClose }) => {
  const isBottom = y > window.innerHeight * 0.6;
  const style = { left: x, top: isBottom ? 'auto' : y, bottom: isBottom ? (window.innerHeight - y) : 'auto' };

  return (
    <div className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-4 z-50 w-72 flex flex-col gap-4" style={style} onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center border-b border-gray-700 pb-2">
        <h3 className="text-white font-bold text-sm">Field</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
      </div>

      <div className="flex gap-2">
        <button onClick={() => onAction('add-offense', pos)} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 rounded text-xs font-bold">Add Offense Here</button>
        <button onClick={() => onAction('add-defense', pos)} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded text-xs font-bold">Add Defense Here</button>
        {onAction('check-walkthrough') && (
          <button onClick={() => onAction('add-ghost-defense', pos)} className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-2 rounded text-xs font-bold border border-gray-400">Add Ghost Defender</button>
        )}
      </div>

      <div className="bg-gray-800/50 rounded p-3 border border-gray-700">
        <div className="text-gray-400 text-[10px] uppercase font-bold mb-3 flex items-center gap-1">
          <span>▼</span> Arrange (selected) {selectedCount > 0 && `(${selectedCount})`}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button disabled={selectedCount < 2} onClick={() => onAction('straight-horiz')} className="bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-gray-800 text-gray-300 border border-gray-600 py-2 rounded text-[10px] transition-colors">Straighten (Horizontal)</button>
          <button disabled={selectedCount < 2} onClick={() => onAction('straight-vert')} className="bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-gray-800 text-gray-300 border border-gray-600 py-2 rounded text-[10px] transition-colors">Straighten (Vertical)</button>
          <button disabled={selectedCount < 2} onClick={() => onAction('space-horiz')} className="bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-gray-800 text-gray-300 border border-gray-600 py-2 rounded text-[10px] transition-colors">Evenly Space (Horiz)</button>
          <button disabled={selectedCount < 2} onClick={() => onAction('space-vert')} className="bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-gray-800 text-gray-300 border border-gray-600 py-2 rounded text-[10px] transition-colors">Evenly Space (Vert)</button>
        </div>
      </div>

      <div className="flex gap-2 border-t border-gray-700 pt-3">
        <button onClick={() => onAction('add-blitz', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" /> Blitz
        </button>
        <button onClick={() => onAction('add-firstdown', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-400 text-[6px] flex items-center justify-center text-black font-bold">1</div> 1st Down
        </button>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onAction('add-linemarker', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <div className="w-2 h-0.5 bg-gray-400" /> Field Line
        </button>
        <button onClick={() => onAction('add-cone', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <span className="text-sm">⚠️</span> Cone
        </button>
        <button onClick={() => onAction('add-los', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <span className="text-sm">📏</span> LOS
        </button>
        <button onClick={() => onAction('add-label', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <span className="text-sm">📝</span> Label
        </button>
      </div>
      <div className="flex gap-2 border-t border-gray-700 pt-3">
        <button onClick={() => onAction('add-freedraw', pos)} className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 py-1.5 rounded text-[10px] flex items-center justify-center gap-1">
          <span className="text-sm">✏️</span> Free Draw
        </button>
      </div>
    </div>
  );
};

function App({ playbookId, onExit, isReadOnly: forceReadOnly, initialPlayId = null, isSinglePlayMode = false }) {
  const { currentUser } = useAuth();
  const { plays: cloudPlays, savePlay: cloudSavePlay, deletePlay } = usePlays(playbookId);
  const { playbooks, inviteUserByEmail, removeCollaborator, updatePlaybook, createPlaybook, deletePlaybook, setPlaybookPublic, fetchPlaybookById } = usePlaybooks();
  const { roster } = useRoster();
  const isCloud = playbookId && playbookId !== 'local';

  const [publicPbData, setPublicPbData] = useState(null);
  const activePbData = (isCloud ? playbooks.find(p => p.id === playbookId) : null) || publicPbData;
  const videoLibrary = useMemo(() => normalizeVideoLibrary(activePbData?.videoLibrary), [activePbData?.videoLibrary]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (isCloud && playbookId && !playbooks.find(p => p.id === playbookId)) {
      fetchPlaybookById(playbookId).then(data => {
        if (!data) setLoadError(true);
        setPublicPbData(data);
      }).catch(() => setLoadError(true));
    }
  }, [playbookId, playbooks, isCloud]);

  useEffect(() => {
    if (loadError) {
      addToast('Could not load playbook. Check sharing permissions.', 'error');
    }
  }, [loadError]);
  const userRole = isCloud ? activePbData?.collaborators?.[currentUser?.uid]?.role : 'owner';

  const isReadOnly = forceReadOnly || (isCloud && activePbData?.ownerId !== currentUser?.uid && !userRole);
  const canEdit = !isReadOnly;

  const [showShareModal, setShowShareModal] = useState(false);
  const [showVideoLibraryModal, setShowVideoLibraryModal] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isNewPlayModalOpen, setIsNewPlayModalOpen] = useState(false);
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);
  const [useRosterForNewPlays, setUseRosterForNewPlays] = useState(true);

  const openVideoLibraryModule = useCallback(() => {
    setShowVideoLibraryModal(true);
    if (typeof window !== 'undefined' && !VIDEO_LIBRARY_HASHES.has(window.location.hash)) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#video-library`);
    }
  }, []);

  const closeVideoLibraryModule = useCallback(() => {
    setShowVideoLibraryModal(false);
    if (typeof window !== 'undefined' && VIDEO_LIBRARY_HASHES.has(window.location.hash)) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncVideoLibraryHash = () => {
      if (VIDEO_LIBRARY_HASHES.has(window.location.hash)) {
        setShowVideoLibraryModal(true);
      }
    };

    syncVideoLibraryHash();
    window.addEventListener('hashchange', syncVideoLibraryHash);
    return () => window.removeEventListener('hashchange', syncVideoLibraryHash);
  }, []);

  const [players, setPlayers] = useState(INITIAL_PLAYERS);
  const [playName, setPlayName] = useState('New Play');
  const [routes, setRoutes] = useState([]);
  const [playVideos, setPlayVideos] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState('');
  const [videoLayout, setVideoLayout] = useState('floating');
  const [videoUploadStatus, setVideoUploadStatus] = useState('');
  const playVideo = useMemo(() => getActiveVideo(playVideos, activeVideoId), [playVideos, activeVideoId]);
  const activeVideoIndex = playVideos.findIndex(video => video.id === playVideo?.id);
  const setVideoStateFromPlay = (source) => {
    const collection = normalizeVideoCollection(source || {});
    setPlayVideos(collection.videos);
    setActiveVideoId(collection.activeVideoId);
  };
  const [selectedTool, setSelectedTool] = useState('select');
  // Split Formations
  const [offenseFormation, setOffenseFormation] = useState('I-Formation');
  const [defenseFormation, setDefenseFormation] = useState(null);
  // Computed for backward compatibility & display
  const currentFormation = offenseFormation + (defenseFormation ? ` vs ${defenseFormation}` : '');

  // Initialize drawing settings including iconSize
  const [drawingSettings, setDrawingSettings] = useState({ color: '#fbbf24', type: 'solid', lineThickness: 0.3, arrowScale: 1 });
  // Global Settings - Expanded for enhanced styling
  const [globalSettings, setGlobalSettings] = useState({
    iconSize: 1.0,
    showRoutes: true,
    offenseColor: '#fbbf24',
    offenseRouteColor: '#fbbf24',
    defenderColor: '#ef4444',
    defenderRouteColor: '#ef4444',
    autoSequentialColor: false,
    showAsNumber: false,
    showPlayerNames: true,
    walkthroughMode: false,
    walkthroughOpacity: 0.2,
    showGridlines: true,
    wristbandGuideEnabled: false,
    theme: 'modern',
    outerCircleMatchColor: true
  });

  const [wristbandGuide, setWristbandGuide] = useState({ enabled: false, x: 50, y: 50, w: 40, h: 22.8 });

  useEffect(() => {
    setWristbandGuide(prev => ({ ...prev, enabled: !!globalSettings.wristbandGuideEnabled }));
  }, [globalSettings.wristbandGuideEnabled]);

  const COLOR_SEQUENCE = [
    '#ef4444', // Red
    '#fbbf24', // Yellow
    '#10b981', // Green
    '#3b82f6', // Blue
    '#8b5cf6', // Violet
    '#f97316', // Orange
    '#06b6d4', // Cyan
    '#ec4899', // Pink
    '#84cc16', // Lime
    '#6366f1'  // Indigo
  ];

  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [showHelpOverlay, setShowHelpOverlay] = useState(false);

  const addToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    if (isReadOnly) {
      setIsLocked(true);
    } else {
      setIsLocked(false);
    }
  }, [isReadOnly]);

  const pushHistory = () => {
    const entry = JSON.stringify({ players, routes, ballSpec, playVideos, activeVideoId });
    setHistory(prev => {
      if (prev.length > 0 && prev[prev.length - 1] === entry) return prev;
      return [...prev, entry].slice(-50);
    });
    if (redoStack.length > 0) setRedoStack([]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const entry = history[history.length - 1];
    const current = JSON.stringify({ players, routes, ballSpec, playVideos, activeVideoId });

    setRedoStack(prev => [...prev, current]);
    setHistory(prev => prev.slice(0, -1));

    const state = JSON.parse(entry);
    setPlayers(state.players);
    setRoutes(state.routes);
    setBallSpec(state.ballSpec);
    setVideoStateFromPlay(state);
    setSelectedPlayerId(null);
    setSelectedPlayerIds(new Set());
    addToast('Play reverted (Undo)', 'info');
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const entry = redoStack[redoStack.length - 1];
    const current = JSON.stringify({ players, routes, ballSpec, playVideos, activeVideoId });

    setHistory(prev => [...prev, current]);
    setRedoStack(prev => prev.slice(0, -1));

    const state = JSON.parse(entry);
    setPlayers(state.players);
    setRoutes(state.routes);
    setBallSpec(state.ballSpec);
    setVideoStateFromPlay(state);
    setSelectedPlayerId(null);
    setSelectedPlayerIds(new Set());
    addToast('Play updated (Redo)', 'info');
  };
  const [contextMenu, setContextMenu] = useState(null);
  const [playbackState, setPlaybackState] = useState({ isPlaying: false, progress: 0, speed: 1, loop: false, duration: 10 });
  const [theme, setTheme] = useState('modern'); // 'modern', 'classic', 'white'

  // Version Modal
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const [ballSpec, setBallSpec] = useState(initialBallSpec);
  const [ballWizardStep, setBallWizardStep] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [inspectedRoute, setInspectedRoute] = useState(null);
  const [smoothingLevel, setSmoothingLevel] = useState(8); // Default to 8 as requested
  const [straightenLevel, setStraightenLevel] = useState(0); // 0 = raw, 10 = max straighten
  const [straightLineMode, setStraightLineMode] = useState(false);


  // PANNING STATE
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  const [pickingMotionRouteId, setPickingMotionRouteId] = useState(null);

  // Playbook Hierarchy State
  const [teamType, setTeamType] = useState('Offense');
  const [currentPlaybook, setCurrentPlaybook] = useState('Default');
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [activeFolderId, setActiveFolderId] = useState(null);
  useEffect(() => {
    setActiveFolderId(null);
  }, [playbookId]);

  // Save status for UI feedback
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedCloudChanges, setHasUnsavedCloudChanges] = useState(false);

  // Manual Reordering Batching
  const [tempOrders, setTempOrders] = useState({});
  const orderSyncTimerRef = useRef(null);

  // New UI Shell State
  const [uiMode, setUiMode] = useState(() => {
    // If we have a specific play to load, go to builder. 
    // If we have a playbook link but NO specific play, go to library.
    if (initialPlayId) return 'builder';
    if (isSinglePlayMode) return 'builder';
    if (playbookId && playbookId !== 'local') return 'library';
    return 'builder';
  });
  const [viewMode, setViewMode] = useState('portrait'); // 'portrait' | 'landscape'
  const [filterType, setFilterType] = useState(new Set());
  const [filterFormation, setFilterFormation] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('order'); // 'order', 'rating', 'newest'

  // Modal State
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    initialValue: '',
    onConfirm: () => { }
  });

  const [playbookData, setPlaybookData] = useState(() => {
    const saved = localStorage.getItem('football_playbook_data');
    if (saved) { return JSON.parse(saved); }
    return {
      'Offense': { playbooks: { 'Default': { formations: { 'I-Formation': { players: OFFENSE_FORMATIONS_6v6['I-Formation'] }, 'Shotgun': { players: OFFENSE_FORMATIONS_6v6['Shotgun'] }, 'Singleback': { players: OFFENSE_FORMATIONS_6v6['Singleback'] }, 'Empty': { players: OFFENSE_FORMATIONS_6v6['Empty'] } }, plays: {}, folders: {} } } },
      'Defense': { playbooks: { 'Default': { formations: {}, plays: {}, folders: {} } } },
      'Special': { playbooks: { 'Default': { formations: {}, plays: {}, folders: {} } } }
    };
  });

  // MIGRATION: Capitalize existing categories and teamType
  useEffect(() => {
    const migrate = () => {
      let changed = false;
      const newData = { ...playbookData };

      // Migrate keys in playbookData
      const oldKeys = ['offense', 'defense', 'special'];
      oldKeys.forEach(oldKey => {
        if (newData[oldKey]) {
          const newKey = oldKey.charAt(0).toUpperCase() + oldKey.slice(1);
          if (!newData[newKey]) {
            newData[newKey] = newData[oldKey];
            delete newData[oldKey];
            changed = true;
          }
        }
      });

      // Migrate teamType state if it's lowercase
      if (oldKeys.includes(teamType)) {
        setTeamType(teamType.charAt(0).toUpperCase() + teamType.slice(1));
      }

      if (changed) {
        setPlaybookData(newData);
      }
    };
    migrate();
  }, []);

  // MIGRATION 2: Ensure all plays have rating and order
  useEffect(() => {
    let changed = false;
    const newData = { ...playbookData };

    Object.values(newData).forEach(team => {
      Object.values(team.playbooks).forEach(pb => {
        if (!pb.folders) pb.folders = {};
        const plays = pb.plays || {};
        const entries = Object.entries(plays);
        entries.forEach(([name, p], idx) => {
          if (p.rating === undefined || p.order === undefined) {
            plays[name] = {
              ...p,
              rating: p.rating ?? 0,
              order: p.order ?? idx
            };
            changed = true;
          }
        });
      });
    });

    if (changed) {
      setPlaybookData(newData);
    }
  }, [playbookData]);

  const [savedPlays, setSavedPlays] = useState(() => {
    const saved = localStorage.getItem('football_plays');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('football_playbook_data', JSON.stringify(playbookData));
  }, [playbookData]);

  // Helper to get current playbook state accurately
  const getCurrentPlaybook = () => {
    let playData;
    if (isCloud) {
      const allFormations = activePbData?.formations || {};
      const sideKey = (teamType || 'offense').toLowerCase() === 'defense' ? 'defense' : 'offense';

      // Categorization check: if nested exists, use it. Otherwise, use flat for legacy support.
      let filteredFormations = {};
      if (allFormations.offense || allFormations.defense) {
        filteredFormations = allFormations[sideKey] || {};
      } else {
        filteredFormations = allFormations; // Legacy flat structure
      }

      // Merge temporary order overrides if any
      const mergedPlays = { ...cloudPlays };
      Object.entries(tempOrders).forEach(([id, order]) => {
        if (mergedPlays[id]) mergedPlays[id] = { ...mergedPlays[id], order };
      });

      playData = {
        id: playbookId,
        formations: filteredFormations,
        folders: activePbData?.folders || {},
        plays: mergedPlays,
        profile: activePbData?.profile || null,
        labelMode: activePbData?.labelMode || 'user',
        customLabels: activePbData?.customLabels || {}
      };
    } else {
      playData = {
        id: currentPlaybook,
        ...(playbookData[teamType]?.playbooks?.[currentPlaybook] || { formations: {}, plays: {} })
      };
    }

    const normalizedProfile = normalizeProfile(playData.profile);
    return {
      ...playData,
      profile: normalizedProfile,
      profileConfig: getProfileConfig(normalizedProfile.profileKey)
    };
  };

  // Computed Filtered List for Navigation (Cycle)
  const activeFolderIds = useMemo(() => {
    if (!activeFolderId) return null;
    const currentPb = getCurrentPlaybook();
    const allFolders = currentPb.folders || {};
    const descendants = [activeFolderId];
    const getChildren = (pid) => {
      Object.values(allFolders).forEach(f => {
        if (f.parentId === pid) {
          descendants.push(f.id);
          getChildren(f.id);
        }
      });
    };
    getChildren(activeFolderId);
    return new Set(descendants);
  }, [activeFolderId, playbookId, playbookData, teamType, currentPlaybook, cloudPlays]);

  const browseablePlays = useMemo(() => {
    const pb = getCurrentPlaybook();
    const plays = pb.plays || {};
    const list = Object.entries(plays).map(([id, p]) => {
      const dateVal = p.updatedAt?.toDate ? p.updatedAt.toDate() : (p.updatedAt || p.savedAt || p.date || null);
      const createdVal = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt || null);
      return {
        id,
        name: p.playName || id,
        formation: p.formation || 'MISC',
        type: p.teamType || 'Offense',
        rating: p.rating || 0,
        order: p.order ?? 0,
        folderId: p.folderId || null,
        savedAt: dateVal,
        createdAt: createdVal
      };
    });

    // Filter
    let filtered = list.filter(it => {
      if (activeFolderIds && !activeFolderIds.has(it.folderId)) return false;
      if (filterType.size) {
        const normalizedFilter = Array.from(filterType).map(t => t.toLowerCase());
        if (!normalizedFilter.includes(it.type.toLowerCase())) return false;
      }
      if (filterFormation.size && !filterFormation.has(it.formation)) return false;
      if (searchTerm && !it.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'rating') {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return a.order - b.order;
      }
      if (sortBy === 'newest') {
        // Creation date (desc)
        return new Date(b.createdAt || b.savedAt || 0) - new Date(a.createdAt || a.savedAt || 0);
      }
      if (sortBy === 'updated') {
        // Update date (desc)
        return new Date(b.savedAt || 0) - new Date(a.savedAt || 0);
      }
      return a.order - b.order;
    });

    return filtered;
  }, [playbookId, playbookData, teamType, currentPlaybook, cloudPlays, searchTerm, filterType, filterFormation, sortBy, activeFolderIds, tempOrders]);


  // Handle Initial Play Deep Link
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  useEffect(() => {
    setHasLoadedInitial(false);
  }, [initialPlayId, playbookId]);

  useEffect(() => {
    if (!initialPlayId || hasLoadedInitial) return;

    const pb = getCurrentPlaybook();
    const allPlays = pb.plays || {};

    // We check cloudPlays specifically to see if synced
    const syncCount = isCloud ? Object.keys(cloudPlays).length : Object.keys(allPlays).length;

    if (syncCount > 0) {
      // Search by ID first, then by playName as fallback
      let targetPlay = allPlays[initialPlayId];
      if (!targetPlay) {
        // Fallback search in values
        targetPlay = Object.values(allPlays).find(p => p.playName === initialPlayId || p.id === initialPlayId);
      }

      if (targetPlay) {
        setPlayers([...targetPlay.players]);
        setRoutes([...targetPlay.routes]);
        setBallSpec(targetPlay.ballSpec ? { ...targetPlay.ballSpec } : initialBallSpec);
        setVideoStateFromPlay(targetPlay);
        setPlayName(targetPlay.playName || initialPlayId);
        setSelectedFormation(targetPlay.formation);
        if (targetPlay.teamType) {
          setTeamType(targetPlay.teamType);
          // Also set local state for teamType if not cloud? 
          // getCurrentPlaybook handles it based on teamType state anyway.
        }
        setSelectedPlayerId(null);
        setHasLoadedInitial(true);
        addToast(`Deep link loaded: ${targetPlay.playName || 'Untitled'}`, 'info');
      } else if (isCloud && syncCount > 0) {
        // We have plays, but none match. Only report once to avoid spam.
        // Actually, wait for more syncs? Let's just stay silent or log once.
      }
    }
  }, [initialPlayId, hasLoadedInitial, cloudPlays, isCloud, playbookData, playbookId]);


  // Walkthrough Mode Safety: Auto-switch to Select tool (Allow offense/defense/draw for "Add Defense/Route" capability)
  useEffect(() => {
    if (globalSettings.walkthroughMode && !['select', 'offense', 'defense', 'draw'].includes(selectedTool)) {
      setSelectedTool('select');
    }
  }, [globalSettings.walkthroughMode, selectedTool]);

  // ... inside App component


  // Helper to access the current profile config easily
  const activeProfileConfig = getCurrentPlaybook().profileConfig;
  const activeLabelMode = getCurrentPlaybook().labelMode || 'user';


  /* ---------------- AUTO-SAVE & SYNC LOGIC ---------------- */
  // Track changes for Cloud Sync
  useEffect(() => {
    if (players.length > 0 || routes.length > 0 || playVideos.length > 0) {
      setHasUnsavedCloudChanges(true);
    }
  }, [players, routes, ballSpec, playVideos, activeVideoId]);

  // Local Auto-save (500ms debounce)
  useEffect(() => {
    if (isReadOnly || !playName) return;
    const timer = setTimeout(() => {
      savePlay(playName, selectedFormation || 'MISC', true);
    }, 500);
    return () => clearTimeout(timer);
  }, [players, routes, ballSpec, playVideos, activeVideoId]);

  // Cloud Sync (30s debounce - Only if changes exist)
  useEffect(() => {
    if (!isCloud || isReadOnly || !playName || !hasUnsavedCloudChanges) return;
    const timer = setTimeout(() => {
      savePlay(playName, selectedFormation || 'MISC', true);
    }, 30000); // 30 seconds as requested
    return () => clearTimeout(timer);
  }, [hasUnsavedCloudChanges]);

  // WALKTHROUGH CLEANUP
  useEffect(() => {
    if (!globalSettings.walkthroughMode) {
      setPlayers(prev => {
        const ghostIds = new Set(prev.filter(p => p.isWalkthrough).map(p => p.id));
        if (ghostIds.size === 0) return prev;
        setRoutes(rPrev => rPrev.filter(r => !ghostIds.has(r.playerId)));
        return prev.filter(p => !p.isWalkthrough);
      });
    }
  }, [globalSettings.walkthroughMode]);

  /* ---------------- ZOOM & PAN LOGIC ---------------- */
  const handleZoom = (delta) => {
    setZoomLevel(prev => {
      const next = prev + delta;
      return Math.max(0.5, Math.min(2.5, next));
    });
  };
  const resetZoom = () => { setZoomLevel(1); setPan({ x: 0, y: 0 }); };

  // Pan Handlers
  const handlePanStart = (e) => {
    // Determine input type and coordinates
    const isTouch = e.touches && e.touches.length > 0;
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;

    const isBackground = e.target.closest('.field-bg') || e.target.classList.contains('main-stage') || e.target.classList.contains('football-field');

    // Allow Middle Button (1), Left Button (0) on background, or Touch (implied background due to stopPropagation on tokens)
    if (isTouch || e.button === 1 || (e.button === 0 && isBackground)) {
      if (!isTouch) e.preventDefault(); // Prevent text selection on mouse drag
      setIsPanning(true);
      panStartRef.current = { x: clientX - pan.x, y: clientY - pan.y };
    }
  };

  const handlePanMove = (e) => {
    if (!isPanning) return;
    const isTouch = e.touches && e.touches.length > 0;
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;

    if (e.cancelable && (isTouch || e.preventDefault)) e.preventDefault();

    setPan({
      x: clientX - panStartRef.current.x,
      y: clientY - panStartRef.current.y
    });
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  // Keyboard Pan
  useEffect(() => {
    const handleKeys = (e) => {
      if (e.key === 'Escape') {
        setContextMenu(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        handleRedo();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isLocked) {
        // Find if we are focused on an input - if so, don't delete
        if (!['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
          if (selectedPlayerId) {
            pushHistory();
            setPlayers(players.filter(p => p.id !== selectedPlayerId));
            setRoutes(routes.filter(r => r.playerId !== selectedPlayerId));
            setSelectedPlayerId(null);
          }
        }
      }

      if (e.code === 'Space') {
        if (!['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
          e.preventDefault();
          togglePlay();
        }
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        // Prevent default scrolling
        e.preventDefault();
        setPan(prev => {
          const step = 20;
          switch (e.key) {
            case 'ArrowUp': return { ...prev, y: prev.y + step };
            case 'ArrowDown': return { ...prev, y: prev.y - step };
            case 'ArrowLeft': return { ...prev, x: prev.x + step };
            case 'ArrowRight': return { ...prev, x: prev.x - step };
            default: return prev;
          }
        });
      }
    };
    window.addEventListener('keydown', handleKeys);
    return () => window.removeEventListener('keydown', handleKeys);
  }, [selectedPlayerId, isLocked, players, routes, ballSpec, togglePlay]);


  /* ---------------- HELPERS ---------------- */
  const getDefaultPlayerSpeed = (type, role) => {
    const currentPb = getCurrentPlaybook();
    const config = currentPb.profileConfig;
    const speedProfile = SPEED_PROFILE_40S[config?.speedProfileKey] || SPEED_PROFILE_40S['flag_12u'];

    // Map role to speed profile key
    const effectiveRole = role || (type === 'offense' ? 'WR' : 'DEF');
    const speedKey = effectiveRole === 'R' ? 'DEF' : effectiveRole;

    return speedProfile[speedKey] || speedProfile['DEF'] || 6.0;
  };

  /* ---------------- NAV LOGIC ---------------- */
  const handleCyclePlay = (direction) => {
    const playsList = browseablePlays;
    if (playsList.length === 0) return;

    // Find current index based on matching playName or ID
    let currentIndex = -1;
    if (isCloud) {
      // In cloud, we search by ID if possible or by name fallback
      currentIndex = playsList.findIndex(p => p.name === playName);
    } else {
      // Local uses name as key
      currentIndex = playsList.findIndex(p => p.id === playName);
    }

    if (currentIndex === -1) {
      // Fallback: Start at beginning if current not found in filtered list
      loadPlay(playsList[0].id);
      return;
    }

    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = playsList.length - 1;
    if (nextIndex >= playsList.length) nextIndex = 0;

    loadPlay(playsList[nextIndex].id);
  };

  const currentPlaysList = Object.keys(getCurrentPlaybook().plays || {});

  // ... (Management Actions - Omitted for brevity but assumed present)
  // Re-implementing simplified versions for overwrite safety if needed, 
  // but relying on partial overwrite requires `multi_replace`. 
  // I am overwriting the WHOLE file, so I need to put the management actions back.

  const openModal = (title, initialValue, onConfirm) => {
    setModalConfig({ isOpen: true, title, initialValue, onConfirm: (val) => { if (!val.trim()) return alert('Name cannot be empty.'); onConfirm(val); setModalConfig(prev => ({ ...prev, isOpen: false })); } });
  };
  const handleCreateType = (name) => {
    if (playbookData[name]) return alert('Category already exists!');
    setPlaybookData(prev => ({ ...prev, [name]: { playbooks: { 'Default': { formations: {}, plays: {} } } } }));
    setTeamType(name); setTimeout(() => setCurrentPlaybook('Default'), 0);
  };
  const handleRenameType = (oldName, newName) => {
    if (playbookData[newName]) return alert('Category name already exists!');
    setPlaybookData(prev => { const n = { ...prev }; n[newName] = n[oldName]; delete n[oldName]; return n; });
    if (teamType === oldName) setTeamType(newName);
  };
  const handleCreatePlaybook = (name) => {
    if (playbookData[teamType]?.playbooks?.[name]) return alert('Playbook already exists!');
    setPlaybookData(prev => ({ ...prev, [teamType]: { ...prev[teamType], playbooks: { ...prev[teamType].playbooks, [name]: { formations: {}, plays: {} } } } }));
    setCurrentPlaybook(name);
  };
  const handleRenamePlaybook = (oldName, newName) => {
    if (playbookData[teamType]?.playbooks?.[newName]) return alert('Playbook name already exists!');
    setPlaybookData(prev => { const pbs = { ...prev[teamType].playbooks }; pbs[newName] = pbs[oldName]; delete pbs[oldName]; return { ...prev, [teamType]: { ...prev[teamType], playbooks: pbs } }; });
    if (currentPlaybook === oldName) setCurrentPlaybook(newName);
  };
  const handleRenameFormation = (oldName, newName) => {
    const pb = getCurrentPlaybook();
    if (pb.formations[newName]) return alert('Formation name already exists!');
    setPlaybookData(prev => { const cPB = prev[teamType].playbooks[currentPlaybook]; const nF = { ...cPB.formations }; nF[newName] = nF[oldName]; delete nF[oldName]; return { ...prev, [teamType]: { ...prev[teamType], playbooks: { ...prev[teamType].playbooks, [currentPlaybook]: { ...cPB, formations: nF } } } }; });
    if (offenseFormation === oldName) setOffenseFormation(newName);
    if (defenseFormation === oldName) setDefenseFormation(newName);
  };
  const handleCreateFormation = (name) => {
    const pb = getCurrentPlaybook();
    if (pb.formations[name]) return alert('Formation already exists!');
    const defaultPlayers = OFFENSE_FORMATIONS_6v6['I-Formation'].map(p => ({ ...p, id: Date.now() + Math.random() }));
    setPlaybookData(prev => ({ ...prev, [teamType]: { ...prev[teamType], playbooks: { ...prev[teamType].playbooks, [currentPlaybook]: { ...prev[teamType].playbooks[currentPlaybook], formations: { ...prev[teamType].playbooks[currentPlaybook].formations, [name]: { players: defaultPlayers } } } } } }));
    alert(`Formation "${name}" created.`);
  };
  const handleTypeChangeSelector = (newType) => { if (!playbookData[newType]) return; setTeamType(newType); const pbs = Object.keys(playbookData[newType].playbooks || {}); if (pbs.length > 0) { if (!playbookData[newType].playbooks[currentPlaybook]) { setCurrentPlaybook(pbs[0]); } } else { setCurrentPlaybook('Default'); } };
  const handlePlaybookChangeSelector = (newPlaybook) => { setCurrentPlaybook(newPlaybook); };

  const handleFormationChangeSelector = (formationName, passedTeamType = null) => {
    const typeToUse = passedTeamType || teamType;
    if (typeToUse === 'Special') return;

    // Determine which set of formations to use based on profile
    const currentPb = getCurrentPlaybook();
    const config = currentPb.profileConfig; // Derived from normalizeProfile() in getCurrentPlaybook()

    // Default to 6v6 if no set key (e.g. tackle profiles or untagged)
    // Actually, for tackle we might want a different default, but for now fallback to 6v6 is safest
    // or we can allow full custom. For this spec, we filter by formationSetKey.
    let formationSet;
    if (config && config.formationSetKey && FORMATIONS_BY_FORMAT[config.formationSetKey]) {
      // e.g. flag_5v5 -> { offense: ..., defense: ... }
      formationSet = FORMATIONS_BY_FORMAT[config.formationSetKey][typeToUse.toLowerCase()] || {};
    } else {
      // Fallback or "Pro/College" where no strict format is enforced in data yet
      // defaulting to the full legacy sets if needed, or keeping it strict?
      // Spec says "Formation menus show ONLY formations valid for the playbook format."
      // So we strictly look up. If null (Pro/College), we might need a "pro" set or default to 6v6 for now.
      // Let's fallback to 6v6 legacy sets for non-flag to ensure *something* shows up.
      formationSet = typeToUse === 'Offense' ? OFFENSE_FORMATIONS_6v6 : DEFENSE_FORMATIONS_6v6;
    }

    // Check custom formations first
    let selectedFormationTemplate = currentPb.formations?.[formationName]?.players;

    // Fallback to presets if not found in custom
    if (!selectedFormationTemplate) {
      selectedFormationTemplate = formationSet[formationName];
    }

    if (selectedFormationTemplate) {
      // Apply formation + Post-placement correction + Speed override
      const speedProfile = SPEED_PROFILE_40S[config?.speedProfileKey] || SPEED_PROFILE_40S['flag_12u'];

      // Map to roster if enabled
      let rosterToUse = [];
      if (useRosterForNewPlays && roster && roster.length > 0) {
        rosterToUse = [...roster].sort((a, b) => (b.overallRank || 0) - (a.overallRank || 0));
      }

      const newPlayers = selectedFormationTemplate.map((p, i) => {
        // 1. Base placement
        let { x, y, role, tag } = p;

        // 2. Post-placement correction "looks right"
        // - clamp x to [5..95]
        x = Math.max(5, Math.min(95, x));

        // - offense WR/SL/C with y<70 -> y=70 (LOS)
        if (typeToUse === 'Offense') {
          if (['WR', 'SL', 'C'].includes(role) && y < 70) y = 70;
        }

        // 3. Speed override
        const effectiveRole = role || (typeToUse === 'Offense' ? 'WR' : 'DEF');
        const speedKey = effectiveRole === 'R' ? 'DEF' : effectiveRole;
        const newSpeed = speedProfile[speedKey] || speedProfile['DEF'] || 5.0;

        const rosterPlayer = rosterToUse[i];

        return {
          ...p,
          id: Date.now() + i,
          x,
          y,
          speed: newSpeed,
          role: effectiveRole,
          tag: tag || '',
          // Roster Overrides
          name: rosterPlayer ? rosterPlayer.name : (p.name || ''),
          number: rosterPlayer ? rosterPlayer.number : (p.number || ''),
          color: rosterPlayer ? rosterPlayer.iconColor : (p.color || (typeToUse === 'Offense' ? globalSettings.offenseColor : globalSettings.defenderColor))
        };
      });

      setPlayers(prev => {
        const others = prev.filter(p => (p.type || '').toLowerCase() !== typeToUse.toLowerCase());
        return [...others, ...newPlayers];
      });
      if (typeToUse === 'Offense') {
        setOffenseFormation(formationName);
        setDefenseFormation(null);
      } else {
        setDefenseFormation(formationName);
      }
      setSelectedFormation(formationName);

      // Auto-save trigger
      // savePlay(playName, formationName, true); // Let the effect handle it
    }
  };

  const handleNewPlay = () => {
    setIsNewPlayModalOpen(true);
  };

  const handleConfirmNewPlay = ({ name, formation, teamType: selectedSide }) => {
    pushHistory();
    setPlayName(name);
    setRoutes([]);
    setBallSpec(initialBallSpec);
    setPlayVideos([]);
    setActiveVideoId('');

    // Get the actual template
    const config = getCurrentPlaybook().profileConfig;
    const setKey = (config && config.formationSetKey) || 'flag_6v6';
    const formationsMap = FORMATIONS_BY_FORMAT[setKey] || FORMATIONS_BY_FORMAT['flag_6v6'];
    const template = selectedSide === 'Offense' ? formationsMap.offense[formation] : formationsMap.defense[formation];

    if (template) {
      // Prioritize roster players if enabled
      let rosterToUse = [];
      if (useRosterForNewPlays && roster && roster.length > 0) {
        rosterToUse = [...roster].sort((a, b) => (b.overallRank || 0) - (a.overallRank || 0));
      }

      const newPlayers = template.map((p, i) => {
        const rosterPlayer = rosterToUse[i];
        return {
          ...p,
          id: Date.now() + i,
          speed: getDefaultPlayerSpeed(p.type, p.role),
          // Override if roster player exists
          name: rosterPlayer ? rosterPlayer.name : (p.name || ''),
          number: rosterPlayer ? rosterPlayer.number : (p.number || ''),
          color: rosterPlayer ? rosterPlayer.iconColor : (p.color || (selectedSide === 'Offense' ? globalSettings.offenseColor : globalSettings.defenderColor))
        };
      });
      setPlayers(newPlayers);
    } else {
      setPlayers([]);
    }

    if (selectedSide === 'Offense') {
      setOffenseFormation(formation);
      setDefenseFormation(null);
      setTeamType('Offense');
    } else {
      setOffenseFormation(null);
      setDefenseFormation(formation);
      setTeamType('Defense');
    }

    setSelectedFormation(formation);
    setIsNewPlayModalOpen(false);
    setUiMode('builder');
  };
  const handleDuplicatePlay = () => {
    if (isCloud && activePbData?.ownerId !== currentUser?.uid && activePbData?.preventDuplication) {
      alert("Duplication is disabled for this playbook by the owner.");
      return;
    }
    openModal('Duplicate Play', `Copy of ${playName}`, (name) => {
      setPlayName(name);
    });
  };

  const handleFlipPlay = () => {
    pushHistory();

    // 1. Flip Players
    setPlayers(prev => prev.map(p => ({
      ...p,
      x: 100 - p.x
    })));

    // 2. Flip Routes
    setRoutes(prev => prev.map(r => ({
      ...r,
      pointsAbs: (r.pointsAbs || []).map(pt => ({
        ...pt,
        x: 100 - pt.x
      })),
      drawBase: r.drawBase ? { ...r.drawBase, x: 100 - r.drawBase.x } : r.drawBase
    })));

    // 3. Flip Ball Start Position
    if (ballSpec && ballSpec.startPos) {
      setBallSpec(prev => ({
        ...prev,
        startPos: { ...prev.startPos, x: 100 - prev.startPos.x }
      }));
    }
  };

  const duplicatePlayToNewName = async (playId, newName) => {
    if (isCloud && activePbData?.ownerId !== currentUser?.uid && activePbData?.preventDuplication) {
      alert("Duplication is disabled for this playbook by the owner.");
      return;
    }

    const pb = getCurrentPlaybook();
    const sourcePlay = pb.plays[playId];
    if (!sourcePlay) return;

    if (isCloud) {
      try {
        const { id, ...rest } = sourcePlay;
        await updatePlaybook(playbookId, { playCount: (activePbData?.playCount || 0) + 1 });
        await cloudSavePlay(null, {
          ...rest,
          playName: newName,
          order: Object.keys(cloudPlays).length,
          folderId: rest.folderId || activeFolderId || null
        }, currentUser);
        alert('Play duplicated successfully!');
      } catch (err) {
        console.error("Duplicate failed", err);
        alert('Failed to duplicate play.');
      }
      return;
    }

    // Local
    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const newPlay = {
        ...sourcePlay,
        playName: newName,
        createdAt: new Date().toISOString(),
        order: Object.keys(pb.plays).length,
        folderId: sourcePlay.folderId || activeFolderId || null
      };
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: { ...pb.plays, [newName]: newPlay },
              playCount: (pb.playCount || 0) + 1
            }
          }
        }
      };
    });
    alert('Play duplicated locally!');
  };

  const handleDeletePlay = async (playId, playNameInput) => {
    if (!window.confirm(`Are you sure you want to delete "${playNameInput}"?`)) return;

    if (isCloud) {
      try {
        await deletePlay(playId);
        await updatePlaybook(playbookId, { playCount: Math.max(0, (activePbData?.playCount || 0) - 1) });
      } catch (err) {
        console.error("Delete failed", err);
        alert("Failed to delete play.");
      }
      return;
    }

    // Local
    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const newPlays = { ...pb.plays };
      delete newPlays[playNameInput];
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: newPlays,
              playCount: Math.max(0, (pb.playCount || 0) - 1)
            }
          }
        }
      };
    });
  };

  const deleteFormation = (formationName) => {
    if (!window.confirm(`Permanently delete custom formation "${formationName}"?`)) return;
    if (isCloud) {
      const sideKey = (teamType || 'offense').toLowerCase() === 'defense' ? 'defense' : 'offense';
      const allFormations = activePbData.formations || {};
      let changed = false;

      if (allFormations.offense || allFormations.defense) {
        // Nested structure: try to delete from current side
        if (allFormations[sideKey] && allFormations[sideKey][formationName]) {
          allFormations[sideKey] = { ...allFormations[sideKey] };
          delete allFormations[sideKey][formationName];
          changed = true;
        }
        // Also check other side just in case (e.g. if side was switched before delete)
        const otherSide = sideKey === 'offense' ? 'defense' : 'offense';
        if (allFormations[otherSide] && allFormations[otherSide][formationName]) {
          allFormations[otherSide] = { ...allFormations[otherSide] };
          delete allFormations[otherSide][formationName];
          changed = true;
        }
      }

      // Always check root for legacy keys or accidental flat saves
      if (allFormations[formationName]) {
        delete allFormations[formationName];
        changed = true;
      }

      if (changed) {
        updatePlaybook(playbookId, { formations: allFormations });
      }
    } else {
      setPlaybookData(prev => {
        const teamData = prev[teamType];
        const pb = teamData.playbooks[currentPlaybook];
        const newFormations = { ...pb.formations };
        delete newFormations[formationName];
        return {
          ...prev,
          [teamType]: {
            ...teamData,
            playbooks: {
              ...teamData.playbooks,
              [currentPlaybook]: {
                ...pb,
                formations: newFormations
              }
            }
          }
        };
      });
    }
    // If we deleted the current formation, switch to something else or null
    if (formationName === offenseFormation) setOffenseFormation(null);
    if (formationName === defenseFormation) setDefenseFormation(null);
    if (formationName === selectedFormation) setSelectedFormation(null);
  };

  const restoreFormationToDefault = (formationName) => {
    if (!formationName) return;

    // Check if it's in the preset library
    const currentPb = getCurrentPlaybook();
    const config = currentPb.profileConfig;
    const setKey = config?.formationSetKey || 'flag_6v6';
    const formationsMap = FORMATIONS_BY_FORMAT[setKey] || FORMATIONS_BY_FORMAT['flag_6v6'];
    const presetOffense = formationsMap.offense[formationName];
    const presetDefense = formationsMap.defense[formationName];

    const isPreset = !!(presetOffense || presetDefense);

    if (isPreset) {
      if (window.confirm(`Reset "${formationName}" to the original library layout? This will clear any manual adjustments you've saved for this formation.`)) {
        // Delete the custom override
        deleteFormation(formationName);
        // Load the preset fallback
        setTimeout(() => loadFormation(formationName), 50);
      }
    } else {
      if (window.confirm(`Reload "${formationName}" from your last saved layout? Unsaved changes to the current positions will be lost.`)) {
        loadFormation(formationName);
      }
    }
  };
  const saveFormation = (formationName) => {
    if (!formationName.trim()) return alert('Enter name');
    const newFormationPlayers = players.filter(p => !p.visualOnly && (p.type || '').toLowerCase() === teamType.toLowerCase()).map(p => ({ ...p, id: Date.now() + Math.random() }));

    if (isCloud) {
      const sideKey = (teamType || 'offense').toLowerCase() === 'defense' ? 'defense' : 'offense';
      const allFormations = activePbData.formations || {};

      const updatedFormations = {
        offense: allFormations.offense || {},
        defense: allFormations.defense || {}
      };

      // If migrating from flat, we don't want to lose them, but we want to start nesting.
      // New saves will go into the appropriate bucket.
      updatedFormations[sideKey][formationName] = { players: newFormationPlayers };

      updatePlaybook(playbookId, { formations: updatedFormations });
    } else {
      setPlaybookData(prev => ({
        ...prev,
        [teamType]: {
          ...prev[teamType],
          playbooks: {
            ...prev[teamType].playbooks,
            [currentPlaybook]: {
              ...prev[teamType].playbooks[currentPlaybook],
              formations: {
                ...prev[teamType].playbooks[currentPlaybook].formations,
                [formationName]: {
                  players: newFormationPlayers
                }
              }
            }
          }
        }
      }));
    }
    alert('Formation layout saved successfully!');
  };
  // Unified Load Logic handles offense/defense split if needed, but for saved full formations (custom), 
  // we might effectively just load them as the active side. 
  // Simplified for now: Load replaces the CURRENT SIDE only.
  const loadFormation = (formationName) => {
    // Guess side based on current teamType or formation lookup?
    // For Quick Formations, we know. For Custom, we assume 'teamType' context.
    handleFormationChangeSelector(formationName, teamType);
  };

  const updatePlayVideo = (patch, videoId = playVideo?.id || activeVideoId) => {
    const targetId = videoId;
    if (!targetId) return;
    setPlayVideos(prev => prev.map(video => (
      video.id === targetId ? { ...video, ...patch, muted: true, updatedAt: new Date().toISOString() } : video
    )));
  };

  const attachVideoFromLibrary = (libraryVideo) => {
    if (!libraryVideo) return;
    const existing = playVideos.find(video => (
      video.libraryVideoId === libraryVideo.id ||
      (video.storagePath && video.storagePath === libraryVideo.storagePath)
    ));
    if (existing) {
      setActiveVideoId(existing.id);
      addToast('Video already attached to this play', 'info');
      return;
    }

    pushHistory();
    const attachment = createVideoAttachment(libraryVideo, playVideos.length);
    setPlayVideos(prev => [...prev, attachment]);
    setActiveVideoId(attachment.id);
    addToast('Video attached to play', 'info');
  };

  const clearPlayVideo = async () => {
    if (!playVideo) return;
    pushHistory();
    const nextVideos = playVideos.filter(video => video.id !== playVideo.id);
    setPlayVideos(nextVideos);
    setActiveVideoId(nextVideos[0]?.id || '');
    addToast('Video removed from this play', 'info');
  };

  const uploadHostedVideo = async (files) => {
    const fileList = Array.from(files || []).filter(Boolean);
    if (fileList.length === 0) return;
    if (!isCloud || !currentUser) {
      alert('Video uploads require a signed-in cloud playbook so QuickRoute can host the file.');
      return;
    }
    const invalidFile = fileList.find(file => !file.type.startsWith('video/'));
    if (invalidFile) {
      alert('Choose video files only.');
      return;
    }
    const oversizedFile = fileList.find(file => file.size > MAX_VIDEO_BYTES);
    if (oversizedFile) {
      alert(`${oversizedFile.name} is over 50 MB. Choose videos up to 50 MB.`);
      return;
    }

    setVideoUploadStatus(fileList.length > 1 ? `Uploading ${fileList.length} videos...` : 'Uploading video...');
    try {
      const uploadedVideos = await Promise.all(fileList.map(async (file) => {
        const libraryId = makeVideoId();
        const path = `play-videos/${currentUser.uid}/${playbookId}/${Date.now()}-${libraryId}-${cleanFileName(file.name)}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file, { contentType: file.type });
        const url = await getDownloadURL(ref);
        return {
          id: libraryId,
          type: 'upload',
          url,
          storagePath: path,
          fileName: file.name,
          label: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          ownerId: currentUser.uid,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }));

      const nextLibrary = [...videoLibrary, ...uploadedVideos];
      await updatePlaybook(playbookId, { videoLibrary: nextLibrary });

      pushHistory();
      const newAttachments = uploadedVideos.map((video, idx) => createVideoAttachment(video, playVideos.length + idx));
      setPlayVideos(prev => [...prev, ...newAttachments]);
      setActiveVideoId(newAttachments[newAttachments.length - 1]?.id || activeVideoId);
      setVideoUploadStatus(fileList.length > 1 ? 'Videos uploaded to library and attached.' : 'Video uploaded to library and attached.');
      addToast(fileList.length > 1 ? 'Videos uploaded' : 'Video uploaded', 'info');
    } catch (err) {
      console.error('Video upload failed:', err);
      alert('Video upload failed. Check Firebase Storage rules and console details.');
      setVideoUploadStatus('');
    }
  };

  const uploadAudioNote = async (blob) => {
    if (!blob || !playVideo) return;
    const targetVideo = playVideo;
    setVideoUploadStatus('Saving audio note...');

    try {
      if (isCloud && currentUser) {
        const contentType = blob.type || 'audio/webm';
        const path = `play-audio-notes/${currentUser.uid}/${playbookId}/${Date.now()}-note.webm`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, blob, { contentType });
        const url = await getDownloadURL(ref);

        updatePlayVideo({
          audioNoteUrl: url,
          audioNoteStoragePath: path,
          audioNoteContentType: contentType,
          audioNoteUpdatedAt: new Date().toISOString()
        }, targetVideo.id);
      } else {
        const dataUrl = await blobToDataUrl(blob);
        updatePlayVideo({
          audioNoteUrl: dataUrl,
          audioNoteStoragePath: '',
          audioNoteContentType: blob.type || 'audio/webm',
          audioNoteUpdatedAt: new Date().toISOString()
        }, targetVideo.id);
      }

      setVideoUploadStatus('Audio note saved. Save the play to keep it attached.');
      addToast('Audio note saved', 'info');
    } catch (err) {
      console.error('Audio note save failed:', err);
      setVideoUploadStatus('');
      addToast('Audio note save failed', 'error');
    }
  };

  const deleteAudioNote = async () => {
    if (!playVideo) return;
    updatePlayVideo({
      audioNoteUrl: '',
      audioNoteStoragePath: '',
      audioNoteContentType: '',
      audioNoteUpdatedAt: ''
    });
    addToast('Audio note removed from this play', 'info');
  };

  const saveVideoTrimAsNewPlay = async () => {
    if (!playVideo || playVideo.type !== 'upload') return;
    const name = window.prompt('Save trimmed video as new play:', `${playName} - Video Cut`);
    if (!name) return;

    const trimmedVideo = {
      ...playVideo,
      trimStart: toSeconds(playVideo.trimStart),
      trimEnd: toSeconds(playVideo.trimEnd),
      sourceStoragePath: playVideo.storagePath,
      savedAsTrimmedClip: true,
      updatedAt: new Date().toISOString()
    };

    const playData = {
      formation: selectedFormation || currentFormation || 'MISC',
      players: players.filter(p => !p.isWalkthrough).map(p => ({ ...p })),
      routes: routes.map(r => ({ ...r })),
      ballSpec: { ...ballSpec },
      video: trimmedVideo,
      videos: [trimmedVideo],
      activeVideoId: trimmedVideo.id,
      updatedAt: new Date().toISOString(),
      teamType,
      playName: name
    };

    if (isCloud) {
      try {
        await cloudSavePlay(null, {
          ...playData,
          rating: 0,
          ratingCount: 0,
          userRatings: {},
          order: Object.keys(cloudPlays).length,
          folderId: activeFolderId || null
        }, currentUser);
        await updatePlaybook(playbookId, { playCount: increment(1) });
        addToast('Saved as a new cloud play', 'info');
      } catch (err) {
        console.error('Failed to save trimmed play:', err);
        addToast('Failed to save trimmed play', 'error');
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: {
                ...pb.plays,
                [name]: {
                  ...playData,
                  savedAt: new Date().toISOString(),
                  createdAt: new Date().toISOString(),
                  rating: 0,
                  order: Object.keys(pb.plays).length,
                  folderId: activeFolderId || null
                }
              }
            }
          }
        }
      };
    });
    addToast('Saved as a new local play', 'info');
  };

  const savePlay = async (playNameInput, formationName = 'MISC', isSilent = false) => {
    if (!playNameInput.trim()) return isSilent ? null : alert('Enter name');

    if (!isSilent) setSaveStatus('saving');

    const playData = {
      formation: formationName,
      players: players.filter(p => !p.isWalkthrough).map(p => ({ ...p })),
      routes: routes.map(r => ({ ...r })),
      ballSpec: { ...ballSpec },
      video: playVideo ? { ...playVideo } : null,
      videos: playVideos.map(video => ({ ...video })),
      activeVideoId: playVideo?.id || activeVideoId || '',
      updatedAt: new Date().toISOString(),
      teamType: teamType,
      playName: playNameInput
    };

    if (isCloud) {
      try {
        const existingPlayId = Object.keys(cloudPlays).find(id => cloudPlays[id].playName === playNameInput);
        const existingPlay = existingPlayId ? cloudPlays[existingPlayId] : null;

        const finalCloudData = {
          ...playData,
          rating: existingPlay?.rating || 0,
          ratingCount: existingPlay?.ratingCount || 0,
          userRatings: existingPlay?.userRatings || {},
          order: existingPlay?.order ?? Object.keys(cloudPlays).length,
          folderId: existingPlay?.folderId || activeFolderId || null
        };

        if (!existingPlayId) {
          await updatePlaybook(playbookId, { playCount: increment(1) });
        }
        await cloudSavePlay(existingPlayId, finalCloudData, currentUser);
        setLastSaved(new Date());
        setSaveStatus('saved');
        setHasUnsavedCloudChanges(false);
        addToast('Play synced to cloud', 'info');
      } catch (err) {
        console.error("Cloud save failed:", err);
        setSaveStatus('error');
        addToast('Cloud sync failed', 'error');
      }
      return;
    }

    // Local Save Logic
    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const existingPlay = pb.plays[playNameInput];

      const newPlay = {
        ...playData,
        savedAt: new Date().toISOString(),
        createdAt: existingPlay?.createdAt || new Date().toISOString(),
        rating: existingPlay?.rating || 0,
        order: existingPlay?.order ?? Object.keys(pb.plays).length,
        folderId: existingPlay?.folderId || activeFolderId || null
      };

      const newPb = {
        ...pb,
        plays: { ...pb.plays, [playNameInput]: newPlay },
        playCount: existingPlay ? pb.playCount : (pb.playCount || 0) + 1
      };

      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: newPb
          }
        }
      };
    });

    setLastSaved(new Date());
    setSaveStatus('saved');
    addToast('Play saved locally', 'info');
  };

  const updatePlayRating = async (playIdentifier, newRating) => {
    if (isCloud) {
      try {
        const play = cloudPlays[playIdentifier];
        if (!play) return;

        const userRatings = { ...(play.userRatings || {}) };
        userRatings[currentUser.uid] = newRating;

        const scores = Object.values(userRatings);
        const count = scores.length;
        const sum = scores.reduce((a, b) => a + b, 0);
        const avg = count > 0 ? sum / count : 0;

        await cloudSavePlay(playIdentifier, {
          ...play,
          rating: avg,
          ratingCount: count,
          userRatings
        }, currentUser);
      } catch (err) {
        console.error("Failed to update cloud rating:", err);
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      if (!pb.plays[playIdentifier]) return prev;

      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: {
                ...pb.plays,
                [playIdentifier]: { ...pb.plays[playIdentifier], rating: newRating, updatedAt: new Date().toISOString() }
              }
            }
          }
        }
      };
    });
  };

  const movePlay = async (playIdentifier, direction) => {
    const list = browseablePlays;
    const idx = list.findIndex(p => p.id === playIdentifier);
    if (idx === -1) return;

    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const p1Id = list[idx].id;
    const p2Id = list[targetIdx].id;

    if (isCloud) {
      const o1 = list[idx].order ?? 0;
      const o2 = list[targetIdx].order ?? 0;

      const newOrders = { ...tempOrders, [p1Id]: o2, [p2Id]: o1 };
      setTempOrders(newOrders);

      if (orderSyncTimerRef.current) clearTimeout(orderSyncTimerRef.current);
      orderSyncTimerRef.current = setTimeout(async () => {
        try {
          const batch = writeBatch(db);
          Object.entries(newOrders).forEach(([id, order]) => {
            const playRef = doc(db, "playbooks", playbookId, "plays", id);
            batch.update(playRef, { order });
          });

          await batch.commit();
          addToast('Manual order synced', 'info');
          // Keep the temp state for 2s to allow snapshot arrival
          setTimeout(() => setTempOrders(prev => {
            if (JSON.stringify(prev) === JSON.stringify(newOrders)) return {};
            return prev;
          }), 2000);
        } catch (e) {
          console.error("Order sync error:", e);
          addToast('Order sync failed', 'error');
        }
      }, 5000);
      return;
    }

    // direction: -1 (up/earlier), 1 (down/later)
    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const plays = { ...pb.plays };

      const p1 = plays[p1Id];
      const p2 = plays[p2Id];
      if (!p1 || !p2) return prev;

      const o1 = p1.order ?? 0;
      const o2 = p2.order ?? 0;

      plays[p1Id] = { ...p1, order: o2 };
      plays[p2Id] = { ...p2, order: o1 };

      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: plays
            }
          }
        }
      };
    });
  };

  const createFolder = async (name, parentId = null) => {
    if (!name.trim()) return;
    const folderId = `folder_${Date.now()}`;
    const now = new Date().toISOString();

    if (isCloud) {
      try {
        const folders = { ...(activePbData?.folders || {}) };
        folders[folderId] = { id: folderId, name, parentId, createdAt: now, updatedAt: now };
        await updatePlaybook(playbookId, { folders });
      } catch (err) {
        console.error("Failed to create cloud folder:", err);
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              folders: { ...pb.folders, [folderId]: { id: folderId, name, parentId, createdAt: now, updatedAt: now } }
            }
          }
        }
      };
    });
  };

  const deleteFolder = async (id) => {
    if (isCloud) {
      try {
        const folders = { ...(activePbData?.folders || {}) };
        const orphanedParentId = folders[id]?.parentId || null;
        delete folders[id];
        Object.keys(folders).forEach(fid => {
          if (folders[fid].parentId === id) folders[fid].parentId = orphanedParentId;
        });

        // Also update plays that were in this folder
        const promises = Object.values(cloudPlays).map(p => {
          if (p.folderId === id) return cloudSavePlay(p.id, { ...p, folderId: orphanedParentId }, currentUser);
          return null;
        }).filter(Boolean);

        await Promise.all([...promises, updatePlaybook(playbookId, { folders })]);
      } catch (err) {
        console.error("Failed to delete cloud folder:", err);
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const newFolders = { ...pb.folders };
      const orphanedParentId = newFolders[id]?.parentId || null;
      delete newFolders[id];
      // Update children to point to deleted folder's parent
      Object.keys(newFolders).forEach(fid => {
        if (newFolders[fid].parentId === id) newFolders[fid].parentId = orphanedParentId;
      });
      const newPlays = { ...pb.plays };
      Object.keys(newPlays).forEach(name => {
        if (newPlays[name].folderId === id) newPlays[name].folderId = orphanedParentId;
      });
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              folders: newFolders,
              plays: newPlays
            }
          }
        }
      };
    });
    if (activeFolderId === id) setActiveFolderId(null);
  };


  const assignPlaysToFolder = async (playIdentifiers, folderId) => {
    if (isCloud) {
      try {
        const promises = playIdentifiers.map(id => {
          const play = cloudPlays[id];
          if (!play) return null;
          return cloudSavePlay(id, { ...play, folderId }, currentUser);
        }).filter(Boolean);
        await Promise.all(promises);
      } catch (err) {
        console.error("Failed to assign cloud plays to folder:", err);
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const newPlays = { ...pb.plays };
      playIdentifiers.forEach(name => {
        if (newPlays[name]) newPlays[name].folderId = folderId;
      });
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: {
              ...pb,
              plays: newPlays
            }
          }
        }
      };
    });
  };
  const loadPlay = (playIdentifier) => {
    // playIdentifier is either the name (local) or the Firestore ID (cloud)
    const pb = getCurrentPlaybook();
    let play = pb.plays?.[playIdentifier];

    // Fallback: If not found by ID (maybe called with name), try searching by name?
    // Actually, LibraryView passes the key/id correctly.
    if (!play) return;

    // Use the stored players and routes directly to preserve internal ID references
    const newPlayers = [...play.players];
    const newRoutes = [...play.routes];

    let newBallSpec = { ...initialBallSpec };
    if (play.ballSpec) {
      newBallSpec = { ...play.ballSpec };
    }

    setPlayers(newPlayers);
    setRoutes(newRoutes);
    setBallSpec(newBallSpec);
    setVideoStateFromPlay(play);
    setPlayName(play.playName || playIdentifier);
    setSelectedFormation(play.formation);
    setSelectedPlayerId(null);
  };
  const openPlayFromLibrary = (name) => { loadPlay(name); setUiMode('builder'); };
  const handleExport = async () => {
    // We capture the '.qr-builder' to include tokens if they are outside main-stage? 
    // No, main-stage usually holds them. But main-stage is inside qr-builder which has the transform.
    // Let's capture the main-stage but we need to ensure the PARENT (qr-builder) doesn't clip or scale it weirdly in the clone.
    // Actually, capturing '.main-stage' is cleaner if we strip the parent's transform influence.
    // BUT, html2canvas renders the element as it appears. If it's scaled down, the screenshot is scaled.
    // Strategy: Capture 'main-stage', but use onclone to ensure it's at scale 1 and fully visible.

    const element = document.querySelector('.main-stage');
    if (!element) return;

    const canvas = await html2canvas(element, {
      useCORS: true,
      scale: 2, // High res
      onclone: (clonedDoc) => {
        // Reset the zoom/pan on the BUILDER container if we were capturing that, OR just ensure main-stage is clean.
        // Since we are selecting .main-stage directly, the parent (.qr-builder) transform *might* not affect the internal rendering context 
        // unless html2canvas walks up. 
        // However, usually html2canvas respects the computed styles.
        // Let's try to capture the .qr-builder (the parent) instead, but reset its transform in the clone.
        // This ensures context is preserved.

        // Wait, the previous code captured .main-stage.
        // If we capture .main-stage, but it's visually shrunk on screen, html2canvas *might* capture it shrunk.
        // Let's force the clone's .main-stage to have no transform (if it had any) or ensure width/height are full.
        // But the zoom is on the PARENT div (.qr-builder). 
        // If we capture the child, the parent's transform technically doesn't apply to the child's node *internally* except for inherited props.

        // BETTER APPROACH identified in Plan:
        // Capture '.qr-builder' (the container with the transform), but Set Transform to None in clone.
        // This gives us the full view.
        // AND we hide the zoom controls in the clone if they are inside.
        // Looking at JSX: qr-builder contains main-stage (and Field). InputModal etc are outside. Perfect.
      }
    });

    // REVISED STRATEGY: Capture the qr-builder (which has the transform), reset it, and crop?
    // Or just capture main-stage? capturing main-stage is safer for cropping.
    // Let's stick to capturing .main-stage but use onclone to find the *cloned* qr-builder (parent) and reset its transform
    // just in case we switch target to .qr-builder or if styles leak.

    // Simplest robust way: 
    // 1. Capture .qr-builder (so we get everything)
    // 2. In onclone, set .qr-builder style.transform = 'none'
    // 3. In onclone, hidden controls? The zoom controls are INSIDE Reference "BuilderPane" but OUTSIDE ".qr-builder".
    //    (Lines 511-516 are absolute div, line 519 is qr-builder).
    //    So catching .qr-builder is SAFE and excludes UI controls. PERFECT.

    const builderEl = document.querySelector('.qr-builder');
    if (!builderEl) return;

    const canvasResult = await html2canvas(builderEl, {
      useCORS: true,
      scale: 2,
      onclone: (clonedDoc) => {
        const clonedBuilder = clonedDoc.querySelector('.qr-builder');
        if (clonedBuilder) {
          clonedBuilder.style.transform = 'none';
          clonedBuilder.style.width = '100%'; // Ensure full size
          clonedBuilder.style.height = '100%';
          // If we had panned far away, the content might be shifted. 
          // But 'main-stage' centers content. 'none' should center it.
          // We might need to ensure justification is center.
          clonedBuilder.style.display = 'flex';
          clonedBuilder.style.alignItems = 'center';
          clonedBuilder.style.justifyContent = 'center';
        }
      }
    });

    const link = document.createElement('a');
    link.download = `${playName.replace(/\s+/g, '_')}.png`;
    link.href = canvasResult.toDataURL();
    link.click();
  };
  const handleRenameFolder = async (id, newName) => {
    const now = new Date().toISOString();
    if (isCloud) {
      try {
        const folders = { ...(activePbData?.folders || {}) };
        if (folders[id]) {
          folders[id] = { ...folders[id], name: newName, updatedAt: now };
          await updatePlaybook(playbookId, { folders });
        }
      } catch (err) {
        console.error("Failed to rename cloud folder:", err);
      }
      return;
    }

    setPlaybookData(prev => {
      const teamData = prev[teamType];
      const pb = teamData.playbooks[currentPlaybook];
      const folders = { ...pb.folders };
      if (folders[id]) folders[id] = { ...folders[id], name: newName, updatedAt: now };
      return {
        ...prev,
        [teamType]: {
          ...teamData,
          playbooks: {
            ...teamData.playbooks,
            [currentPlaybook]: { ...pb, folders }
          }
        }
      };
    });
  };

  const handleRequestModal = (action, context) => {
    switch (action) {
      case 'create-playbook': openModal('Create New Playbook', '', handleCreatePlaybook); break;
      case 'rename-playbook': openModal('Rename Playbook', context, (val) => handleRenamePlaybook(context, val)); break;
      case 'create-type': openModal('Create New Category', '', handleCreateType); break;
      case 'rename-type': openModal('Rename Category', context, (val) => handleRenameType(context, val)); break;
      case 'create-formation': openModal('Create New Formation', '', handleCreateFormation); break;
      case 'rename-formation': openModal('Rename Formation', context, (val) => handleRenameFormation(context, val)); break;
      case 'create-folder': openModal('Create New Folder', '', (name) => createFolder(name, context)); break;
      case 'rename-folder': openModal('Rename Folder', context.name, (val) => handleRenameFolder(context.id, val)); break;
      default: console.warn('Unknown modal action:', action);
    }
  };

  // ... (Effects for Delete/Playback) - Omitted for brevity but assumed present
  useEffect(() => { const closeMenu = () => setContextMenu(null); window.addEventListener('click', closeMenu); return () => window.removeEventListener('click', closeMenu); }, []);

  useEffect(() => {
    const handleGlobalKeys = (e) => {
      // Don't trigger if user is typing in a text field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // ? for Help
      if (e.key === '?' || e.key === '/') {
        if (e.shiftKey || e.key === '?') {
          e.preventDefault();
          setShowHelpOverlay(prev => !prev);
        }
      }

      // Space for Play/Stop
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      }

      // S for Select tool
      if (e.key.toLowerCase() === 's' && !e.ctrlKey && !e.metaKey) {
        setSelectedTool('select');
        addToast('Select Tool', 'info');
      }

      // D for Draw tool
      if (e.key.toLowerCase() === 'd' && !e.ctrlKey && !e.metaKey) {
        setSelectedTool('draw');
        addToast('Draw Tool', 'info');
      }

      // Undo/Redo
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if (e.ctrlKey && (e.key === 'y' || (e.ctrlKey && e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [handleUndo, handleRedo, togglePlay, setSelectedTool]);

  useEffect(() => {
    let animationFrame;
    if (playbackState.isPlaying) {
      let lastTime = Date.now(); const loop = () => { const now = Date.now(); const dt = (now - lastTime) / 1000; lastTime = now; setPlaybackState(prev => { if (!prev.isPlaying) return prev; const DURATION = (prev.duration || 10) / prev.speed; let newProgress = prev.progress + (dt / DURATION); if (newProgress >= 1) { if (prev.loop) { newProgress = 0; } else { newProgress = 1; return { ...prev, isPlaying: false, progress: 1 }; } } return { ...prev, progress: newProgress }; }); animationFrame = requestAnimationFrame(loop); }; animationFrame = requestAnimationFrame(loop);
    } return () => cancelAnimationFrame(animationFrame);
  }, [playbackState.isPlaying, playbackState.speed]);
  // Ramer-Douglas-Peucker Simplification
  const getSqDist = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2;
  const getSqSegDist = (p, p1, p2) => {
    let x = p1.x, y = p1.y, dx = p2.x - x, dy = p2.y - y;
    if (dx !== 0 || dy !== 0) {
      const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
      if (t > 1) { x = p2.x; y = p2.y; }
      else if (t > 0) { x += dx * t; y += dy * t; }
    }
    return (p.x - x) ** 2 + (p.y - y) ** 2;
  };
  const simplifyDPStep = (points, first, last, sqTolerance, simplified) => {
    let maxSqDist = sqTolerance, index = -1;
    for (let i = first + 1; i < last; i++) {
      const sqDist = getSqSegDist(points[i], points[first], points[last]);
      if (sqDist > maxSqDist) { index = i; maxSqDist = sqDist; }
    }
    if (index > -1) {
      if (index - first > 1) simplifyDPStep(points, first, index, sqTolerance, simplified);
      simplified.push(points[index]);
      if (last - index > 1) simplifyDPStep(points, index, last, sqTolerance, simplified);
    }
  };
  const simplifyRoute = (points, tolerance) => {
    if (points.length <= 2) return points;
    const sqTolerance = tolerance * tolerance;
    let result = [points[0]];
    simplifyDPStep(points, 0, points.length - 1, sqTolerance, result);
    result.push(points[points.length - 1]);
    return result;
  };

  const handleSharpenRoutes = () => {
    setRoutes(currentRoutes => currentRoutes.map(r => {
      if (r.isPreset || !r.isFreeHand) return r;
      // Source is raw or abs
      const raw = r.pointsRaw || r.pointsAbs || r.points;
      if (!raw || raw.length < 3) return r;

      // Apply Simplification (Straighten)
      // Tolerance 1.5 yards (approx 1.5 * 1.8 = 2.7 units?)
      // Field is 0-100. 1.5 units is decent tolerance.
      const sharpened = simplifyRoute(raw, 1.2);

      // We update pointsRaw to this new simplified version
      // And we must re-apply smoothing if level > 0 (handled by handleSmoothingChange logic normally, 
      // but here we must do it manually or call the smoother logic)
      // To keep it simple: We set pointsRaw = sharpened.
      // Then we apply the *current* smoothingLevel to generate pointsAbs.

      let smoothed = [...sharpened];
      // Re-apply smoothing based on current level
      for (let iter = 0; iter < smoothingLevel; iter++) {
        const nextPts = [smoothed[0]];
        for (let i = 1; i < smoothed.length - 1; i++) {
          const prev = smoothed[i - 1];
          const curr = smoothed[i];
          const next = smoothed[i + 1];
          nextPts.push({
            x: 0.25 * prev.x + 0.5 * curr.x + 0.25 * next.x,
            y: 0.25 * prev.y + 0.5 * curr.y + 0.25 * next.y
          });
        }
        nextPts.push(smoothed[smoothed.length - 1]);
        smoothed = nextPts;
      }

      return {
        ...r,
        pointsRaw: sharpened, // Update Source of Truth to be the straight segments
        pointsAbs: smoothed
      };
    }));
  };

  // Auto-heal playbook play counts
  useEffect(() => {
    if (isCloud && activePbData && cloudPlays) {
      const actualCount = Object.keys(cloudPlays).length;
      if (activePbData.playCount !== actualCount) {
        updatePlaybook(playbookId, { playCount: actualCount });
      }
    }
  }, [isCloud, activePbData, cloudPlays, playbookId, updatePlaybook]);

  function togglePlay() {
    setPlaybackState(prev => {
      if (prev.progress >= 1 && !prev.loop) {
        return { ...prev, isPlaying: true, progress: 0 };
      }
      return { ...prev, isPlaying: !prev.isPlaying };
    });
  }
  const handleSelectTool = (toolId) => {
    setSelectedTool(toolId);
    if (toolId !== 'library') setInspectedRoute(null);
    setSelectedPlayerId(null);
    if (toolId === 'draw') {
      setDrawingSettings(prev => ({ ...prev, type: 'solid', startPos: null }));
    }
    if (toolId === 'ball') {
      if (!ballSpec.enabled) {
        setBallSpec(initialBallSpec);
      }
      setBallWizardStep(1);
    } else {
      setBallWizardStep(0);
    }
  };
  const handleSmoothingChange = (level) => {
    const safeLevel = Math.max(0, Math.min(10, level));
    setSmoothingLevel(safeLevel);
    setRoutes(currentRoutes => currentRoutes.map(r => processRoute(r, safeLevel, straightenLevel)));
  };

  const handleStraightenChange = (level) => {
    const safeLevel = Math.max(0, Math.min(10, level));
    setStraightenLevel(safeLevel);
    setRoutes(currentRoutes => currentRoutes.map(r => r.isFreeHand ? processRoute(r, smoothingLevel, safeLevel) : r));
  };

  const handleAddPlayerAt = (type, pos) => {
    pushHistory();
    const id = Date.now();
    let color = type === 'offense' ? globalSettings.offenseColor : globalSettings.defenderColor;
    let routeColor = type === 'offense' ? globalSettings.offenseRouteColor : globalSettings.defenderRouteColor;
    let sequentialIndex = null;

    if (globalSettings.autoSequentialColor) {
      const existingIndexes = players.map(p => p.sequentialIndex || 0);
      sequentialIndex = existingIndexes.length > 0 ? Math.max(...existingIndexes) + 1 : 1;
      color = COLOR_SEQUENCE[(sequentialIndex - 1) % COLOR_SEQUENCE.length];
      // Default route color to match the sequence if sequential is on? 
      // User said "Add each player as different color", usually implies route color follows or stays default.
      // Let's keep routeColor as the global default unless they change it.
    }

    setPlayers([...players, {
      id,
      type,
      x: pos.x,
      y: pos.y,
      label: type === 'offense' ? 'P' : 'X',
      role: type === 'offense' ? 'WR' : 'DEF',
      speed: getDefaultPlayerSpeed(type, type === 'offense' ? 'WR' : 'DEF'),
      color,
      routeColor,
      sequentialIndex
    }]);
    setContextMenu(null);
  };

  const handleAddMarkerAt = (type, pos) => {
    pushHistory();
    const id = Date.now();
    // Special handling for LOS: Only one allowed
    if (type === 'los') {
      const others = players.filter(p => p.type !== 'los');
      setPlayers([...others, { id, type, x: pos.x, y: pos.y, label: 'LOS', speed: 0, visualOnly: true }]);
    } else if (type === 'freedraw') {
      // Free Draw creates a player that is immediately selected for drawing
      const newPlayer = { id, type, x: pos.x, y: pos.y, label: '', speed: 0, visualOnly: true };
      setPlayers([...players, newPlayer]);
      setSelectedPlayerId(id);
      setSelectedPlayerIds(new Set([id]));
      // Switch to draw tool immediately
      setSelectedTool('draw');
    } else {
      setPlayers([...players, { id, type, x: pos.x, y: pos.y, label: '', speed: 0, visualOnly: true }]);
    }
    setContextMenu(null);
  };

  const handleClonePlayer = (id) => {
    pushHistory();
    const p = players.find(pl => pl.id === id);
    if (!p) return;
    const newId = Date.now() + Math.floor(Math.random() * 1000);
    const newPlayer = { ...p, id: newId, x: p.x + 2, y: p.y + 2 };
    // Copy routes
    const existingRoute = routes.find(r => r.playerId === id);
    const newRoutes = [...routes];
    if (existingRoute) {
      newRoutes.push({ ...existingRoute, id: Date.now() + 2000, playerId: newId });
    }
    setPlayers([...players, newPlayer]);
    setRoutes(newRoutes);
    setContextMenu(null);
    setSelectedPlayerId(newId);
    setSelectedPlayerIds(new Set([newId]));
  };

  const handleArrange = (type) => {
    const selected = players.filter(p => selectedPlayerIds.has(p.id));
    if (selected.length < 2) return;

    pushHistory();
    let newPlayers = [...players];

    if (type === 'straight-horiz') {
      const avgY = selected.reduce((sum, p) => sum + p.y, 0) / selected.length;
      newPlayers = players.map(p => selectedPlayerIds.has(p.id) ? { ...p, y: avgY } : p);
    } else if (type === 'straight-vert') {
      const avgX = selected.reduce((sum, p) => sum + p.x, 0) / selected.length;
      newPlayers = players.map(p => selectedPlayerIds.has(p.id) ? { ...p, x: avgX } : p);
    } else if (type === 'space-horiz') {
      const sorted = [...selected].sort((a, b) => a.x - b.x);
      const minX = sorted[0].x;
      const maxX = sorted[sorted.length - 1].x;
      const step = (maxX - minX) / (sorted.length - 1);
      newPlayers = players.map(p => {
        if (!selectedPlayerIds.has(p.id)) return p;
        const idx = sorted.findIndex(s => s.id === p.id);
        return { ...p, x: minX + idx * step };
      });
    } else if (type === 'space-vert') {
      const sorted = [...selected].sort((a, b) => a.y - b.y);
      const minY = sorted[0].y;
      const maxY = sorted[sorted.length - 1].y;
      const step = (maxY - minY) / (sorted.length - 1);
      newPlayers = players.map(p => {
        if (!selectedPlayerIds.has(p.id)) return p;
        const idx = sorted.findIndex(s => s.id === p.id);
        return { ...p, y: minY + idx * step };
      });
    }
    setPlayers(newPlayers);
    setContextMenu(null);
  };



  const handleLabelModeChange = async (newMode) => {
    if (isCloud) {
      if (playbookId) {
        await updatePlaybook(playbookId, { labelMode: newMode });
      }
    } else {
      setPlaybookData(prev => {
        const teamData = prev[teamType];
        const pb = teamData.playbooks[currentPlaybook];
        return {
          ...prev,
          [teamType]: {
            ...teamData,
            playbooks: {
              ...teamData.playbooks,
              [currentPlaybook]: { ...pb, labelMode: newMode }
            }
          }
        };
      });
    }
  };

  return (
    <div className={`qr-shell theme-${theme}`}>
      <InputModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        initialValue={modalConfig.initialValue}
        onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={modalConfig.onConfirm}
      />

      <NewPlayModal
        isOpen={isNewPlayModalOpen}
        onClose={() => setIsNewPlayModalOpen(false)}
        onConfirm={handleConfirmNewPlay}
        availableOffenseFormations={(() => {
          const config = getCurrentPlaybook().profileConfig;
          const k = config?.formationSetKey || 'flag_6v6';
          return Object.keys(FORMATIONS_BY_FORMAT[k]?.offense || OFFENSE_FORMATIONS_6v6);
        })()}
        availableDefenseFormations={(() => {
          const config = getCurrentPlaybook().profileConfig;
          const k = config?.formationSetKey || 'flag_6v6';
          return Object.keys(FORMATIONS_BY_FORMAT[k]?.defense || DEFENSE_FORMATIONS_6v6);
        })()}
        currentOffenseFormation={offenseFormation}
        currentDefenseFormation={defenseFormation}
        existingPlayNames={Object.values(isCloud ? cloudPlays : (getCurrentPlaybook().plays || {})).map(p => p.playName)}
      />

      <HeaderBar
        appName="QuickRoute"
        uiMode={uiMode}
        setUiMode={setUiMode}
        activePlaybook={isCloud ? (activePbData?.name || (loadError ? 'Private' : 'Loading...')) : currentPlaybook}
        setActivePlaybook={handlePlaybookChangeSelector}
        playbooks={isCloud ? playbooks.map(p => p.name) : Object.keys(playbookData[teamType]?.playbooks || {})}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        togglePlay={togglePlay}
        onSavePlay={canEdit ? () => savePlay(playName, selectedFormation) : null}
        onExportImage={handleExport}
        currentPlayName={playName === 'New Play' ? '' : playName}
        onPrevPlay={() => handleCyclePlay(-1)}
        onNextPlay={() => handleCyclePlay(1)}
        hasPlays={browseablePlays.length > 0}
        playbackState={playbackState}
        onRequestModal={canEdit ? (type, val) => {
          if (type === 'create-playbook') openModal('Create Playbook', val, handleCreatePlaybook);
          if (type === 'rename-playbook') openModal('Rename Playbook', val, handleRenamePlaybook);
        } : null}
        theme={globalSettings.theme}
        setTheme={(t) => setGlobalSettings(s => ({ ...s, theme: t }))}
        viewMode={viewMode}
        setViewMode={setViewMode}
        canUndo={canEdit && history.length > 0}
        canRedo={canEdit && redoStack.length > 0}
        onUndo={handleUndo}
        onRedo={handleRedo}
        isLocked={isLocked}
        setIsLocked={canEdit ? setIsLocked : () => { }}
        onExit={onExit}
        onShare={isCloud && canEdit ? () => setShowShareModal(true) : null}
        onOpenVideoLibrary={openVideoLibraryModule}
        videoCount={videoLibrary.length}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        isCloud={isCloud}
        isShared={isCloud && (activePbData?.collaboratorIds?.length > 1 || activePbData?.isPublic)}
        labelMode={activeLabelMode}
        setLabelMode={handleLabelModeChange}
        onOpenPrint={() => setIsPrintModalOpen(true)}
        onCopyPlayLink={() => {
          const pb = getCurrentPlaybook();
          const allPlaysMap = pb.plays || {};
          // Find the one matching current name
          const targetId = Object.keys(allPlaysMap).find(id => allPlaysMap[id].playName === playName || id === playName);

          if (targetId && isCloud) {
            const url = `${window.location.origin}/?play=${playbookId}:${targetId}`;
            navigator.clipboard.writeText(url);
            addToast('Link copied to clipboard!', 'info');
          } else if (!isCloud) {
            addToast('Sharing is only available for Cloud Playbooks.', 'info');
          } else {
            addToast('Save the play first to share it.', 'error');
          }
        }}
        isSinglePlayMode={isSinglePlayMode}
      />

      {uiMode === 'builder' && (
        <button
          type="button"
          className="qr-video-module-fab"
          onClick={openVideoLibraryModule}
          title="Open Video Library"
        >
          Videos
          {videoLibrary.length > 0 && <span>{videoLibrary.length}</span>}
        </button>
      )}

      <div className="qr-body">
        <aside className="qr-left-rail">
          {/* Team Roster Button */}
          <div className="p-3 border-b border-gray-800">
            <button
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl bg-gradient-to-r from-blue-600/20 to-indigo-600/10 border border-blue-500/30 hover:border-blue-500/60 transition-all group shadow-lg shadow-blue-900/10 active:scale-[0.98]"
              onClick={() => setIsRosterModalOpen(true)}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl group-hover:scale-110 transition-transform">📋</span>
                <div className="text-left">
                  <div className="text-xs font-black text-white uppercase tracking-tight">Team Roster</div>
                  <div className="text-[9px] text-blue-400 font-bold uppercase tracking-widest">Manage Players</div>
                </div>
              </div>
              <span className="text-gray-600 group-hover:text-blue-400">→</span>
            </button>
            <label className="flex items-center gap-2 mt-2 px-1 text-[10px] text-gray-500 font-bold uppercase cursor-pointer hover:text-gray-400">
              <input
                type="checkbox"
                checked={useRosterForNewPlays}
                onChange={(e) => setUseRosterForNewPlays(e.target.checked)}
                className="w-3 h-3 rounded border-gray-700 bg-gray-800 accent-blue-600"
              />
              Use for New Plays
            </label>
          </div>
          {uiMode === 'library' && !isSinglePlayMode ? (
            <QuickRoutesPanel
              teamType={teamType}
              setTeamType={setTeamType}
              availableTypes={Object.keys(playbookData)}
              formations={Object.keys(getCurrentPlaybook().formations || {})}
              filterType={filterType}
              setFilterType={setFilterType}
              filterFormation={filterFormation}
              setFilterFormation={setFilterFormation}
              onRequestModal={handleRequestModal}
              // Folder Props
              playbook={getCurrentPlaybook()}
              activeFolderId={activeFolderId}
              setActiveFolderId={setActiveFolderId}
              onCreateFolder={createFolder}
              onDeleteFolder={deleteFolder}
              onAssignPlays={assignPlaysToFolder}
              onDuplicatePlay={(playId, name) => openModal('Duplicate Play', `Copy of ${name}`, (newVal) => duplicatePlayToNewName(playId, newVal))}
              canDuplicate={(!isCloud || activePbData?.ownerId === currentUser?.uid || !activePbData?.preventDuplication)}
            />
          ) : (
            <LeftRailBuilder
              selectedTool={selectedTool}
              setSelectedTool={handleSelectTool}
              onHoverRoute={setInspectedRoute}
              drawingSettings={drawingSettings}
              setDrawingSettings={setDrawingSettings}

              smoothingLevel={smoothingLevel}
              setSmoothingLevel={handleSmoothingChange}
              straightenLevel={straightenLevel}
              setStraightenLevel={handleStraightenChange}
              straightLineMode={straightLineMode}
              setStraightLineMode={setStraightLineMode}
              clearRoutes={() => setRoutes([])}
              onFlipPlay={handleFlipPlay}
              teamType={teamType}
              playbookName={isCloud ? (activePbData?.name || playbookId) : currentPlaybook}
              playName={playName}
              offenseFormation={offenseFormation}
              defenseFormation={defenseFormation}
              availableTypes={Object.keys(playbookData)}
              availablePlaybooks={isCloud ? [activePbData?.name || playbookId] : Object.keys(playbookData[teamType]?.playbooks || {})}
              availableOffenseFormations={(() => {
                const config = getCurrentPlaybook().profileConfig;
                if (config && config.formationSetKey && FORMATIONS_BY_FORMAT[config.formationSetKey]) {
                  return Object.keys(FORMATIONS_BY_FORMAT[config.formationSetKey].offense);
                }
                // Fallback: Show only 6v6 if unknown, OR custom logic? 
                // Spec says "Formation menus show ONLY formations valid for the playbook format."
                // Default fallback to 6v6 to avoid clutter.
                return Object.keys(OFFENSE_FORMATIONS_6v6);
              })()}
              availableDefenseFormations={(() => {
                const config = getCurrentPlaybook().profileConfig;
                if (config && config.formationSetKey && FORMATIONS_BY_FORMAT[config.formationSetKey]) {
                  return Object.keys(FORMATIONS_BY_FORMAT[config.formationSetKey].defense);
                }
                return Object.keys(DEFENSE_FORMATIONS_6v6);
              })()}
              onTypeChange={handleTypeChangeSelector}
              onPlaybookChange={isCloud ? null : handlePlaybookChangeSelector}
              onFormationChange={handleFormationChangeSelector}
              onNewPlay={handleNewPlay}
              onDuplicatePlay={handleDuplicatePlay}
              canDuplicate={(!isCloud || activePbData?.ownerId === currentUser?.uid || !activePbData?.preventDuplication)}
              onSavePlay={() => savePlay(playName, selectedFormation || 'MISC')}
              globalSettings={globalSettings}
              setGlobalSettings={setGlobalSettings}
              roster={roster}
            />
          )}
        </aside>

        <main className="qr-center" style={{ overflow: 'hidden' }}>
          {uiMode === 'library' ? (
            <QuickRoutesView
              playbook={getCurrentPlaybook()}
              searchTerm={searchTerm}
              filterType={filterType}
              filterFormation={filterFormation}
              onOpen={openPlayFromLibrary}
              onUpdateRating={updatePlayRating}
              onMovePlay={movePlay}
              onDuplicatePlay={(playId, name) => openModal('Duplicate Play', `Copy of ${name}`, (newVal) => duplicatePlayToNewName(playId, newVal))}
              onDeletePlay={handleDeletePlay}
              canDuplicate={(!isCloud || activePbData?.ownerId === currentUser?.uid || !activePbData?.preventDuplication)}
              canDelete={(!isCloud || activePbData?.ownerId === currentUser?.uid)}
              activeFolderId={activeFolderId}
              onAssignPlays={assignPlaysToFolder}
              sortBy={sortBy}
              setSortBy={setSortBy}
              addToast={addToast}
            />
          ) : (
            <BuilderPane>
              {/* Ball Sequence Status Overlay (Top-Left of Builder Area) */}
              {selectedTool === 'ball' && ballWizardStep >= 2 && ballWizardStep < 4 && (
                <div className="absolute top-4 left-4 z-50 flex flex-col gap-2 pointer-events-none">
                  <div className="bg-slate-900/90 backdrop-blur-md border border-blue-500/30 rounded-lg p-3 shadow-xl w-64 animate-in fade-in slide-in-from-left-4 duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                        Ball Play Logic
                      </span>
                      <span className="text-[9px] text-slate-500 font-bold bg-slate-800 px-1.5 py-0.5 rounded uppercase">Step {ballWizardStep}/3</span>
                    </div>

                    <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                      {(() => {
                        const summary = [];
                        ballSpec.segments?.forEach((seg, i) => {
                          if (seg.kind === 'snap') {
                            const to = players.find(p => p.id === seg.toPlayerId);
                            summary.push({ id: `snap-${i}`, label: `SNAP to ${to?.role || 'QB'}`, icon: '🏈' });
                          } else if (seg.kind === 'handoff') {
                            const to = players.find(p => p.id === seg.toPlayerId);
                            summary.push({ id: `handoff-${i}`, label: `HANDOFF to ${to?.role || to?.label || 'Player'}`, icon: '🖐️' });
                          } else if (seg.kind === 'throw') {
                            const to = players.find(p => p.id === seg.toPlayerId);
                            summary.push({ id: `throw-${i}`, label: `PASS to ${to?.role || to?.label || 'Player'}`, icon: '🎯' });
                          }
                        });
                        return summary;
                      })().map((item) => (
                        <div key={item.id} className="flex items-center gap-3 px-2 py-1.5 rounded bg-white/5 border border-white/5">
                          <span className="text-sm">{item.icon}</span>
                          <span className="text-[11px] font-semibold text-white truncate">{item.label}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="text-[10px] text-slate-400 font-medium italic leading-relaxed">
                        {ballWizardStep === 2 && "Click an offensive player (e.g. QB) to receive the initial SNAP."}
                        {ballWizardStep === 3 && "Click a player to HANDOFF, or click a route path to PASS."}
                      </div>
                      {ballWizardStep === 3 && (
                        <div className="mt-2 text-[10px] text-amber-400 font-black flex items-center gap-1 uppercase">
                          <span className="w-1 h-1 rounded-full bg-amber-500" />
                          Right-Click to Finish
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {pickingMotionRouteId && (
                <div className="absolute inset-0 bg-indigo-900/20 z-[60] pointer-events-none border-4 border-indigo-500/30 flex items-center justify-center">
                  <div className="bg-indigo-950 border-2 border-indigo-400 p-6 rounded-xl shadow-2xl flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-300">
                    <span className="text-4xl animate-bounce">📍</span>
                    <div className="text-white text-base font-black uppercase tracking-widest text-center">
                      Select Snap Point<br />
                      <span className="text-xs text-indigo-300 font-normal normal-case">Click on the route to set the snap location</span>
                    </div>
                    <button
                      className="mt-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold px-6 py-2 rounded-full pointer-events-auto transition-all hover:scale-105 active:scale-95"
                      onClick={() => setPickingMotionRouteId(null)}
                    >
                      Cancel Selection
                    </button>
                  </div>
                </div>
              )}

              <div className="absolute top-4 right-4 z-40 flex flex-col gap-1.5 bg-gray-900/90 border border-gray-700 rounded-xl p-2 shadow-2xl backdrop-blur-md">
                <button title="Zoom In" onClick={() => handleZoom(0.1)} className="w-8 h-8 flex items-center justify-center text-white hover:bg-gray-800 rounded-lg transition-colors font-bold text-lg">+</button>
                <button title="Zoom Out" onClick={() => handleZoom(-0.1)} className="w-8 h-8 flex items-center justify-center text-white hover:bg-gray-800 rounded-lg transition-colors font-bold text-lg">-</button>
                <div className="h-px bg-gray-700 my-0.5" />
                <button title="Reset Zoom & Pan" onClick={resetZoom} className="w-8 h-8 flex items-center justify-center text-[10px] text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors font-black">100%</button>
                <div className="text-[10px] text-center font-bold text-blue-500/80 mt-0.5">{Math.round(zoomLevel * 100)}%</div>
              </div>

              {/* BUILDER AREA: Pan Handlers Attached Here */}
              <div
                className="qr-builder"
                style={{
                  // Apply Zoom AND Pan Transform
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
                  transformOrigin: 'center center', // Changed to center for better panning feel? Or keep top center.
                  transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                  height: '100%',
                  width: playVideo?.type === 'upload' && videoLayout === 'side' ? `calc(100% - ${getVideoPanelWidth(playVideo) + 32}px)` : '100%',
                  cursor: isPanning ? 'grabbing' : selectedTool === 'select' ? 'grab' : 'default'
                }}
                onMouseDown={handlePanStart}
                onTouchStart={handlePanStart}
                onMouseMove={handlePanMove}
                onTouchMove={handlePanMove}
                onMouseUp={handlePanEnd}
                onTouchEnd={handlePanEnd}
                onMouseLeave={handlePanEnd}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSelectedTool('select');
                  setContextMenu(null);
                  setSelectedPlayerId(null);
                }}
              >
                <div className="main-stage field-bg" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Field
                    players={players}
                    setPlayers={setPlayers}
                    selectedTool={selectedTool}
                    routes={routes}
                    setRoutes={setRoutes}
                    drawingSettings={drawingSettings}
                    setDrawingSettings={setDrawingSettings}
                    straightLineMode={straightLineMode}
                    savedPlays={savedPlays}
                    selectedPlayerId={selectedPlayerId}
                    setSelectedPlayerId={setSelectedPlayerId}
                    selectedPlayerIds={selectedPlayerIds}
                    setSelectedPlayerIds={setSelectedPlayerIds}
                    globalSettings={globalSettings}
                    theme={globalSettings.theme}
                    onContextMenu={(e, type, targetId, pos) => {
                      if (type === 'exit-draw') {
                        handleSelectTool('select');
                        setBallWizardStep(1);
                        return;
                      }
                      const box = e.currentTarget.getBoundingClientRect();
                      setContextMenu({ type, targetId, x: e.clientX, y: e.clientY, pos });
                    }}
                    playbackState={playbackState}
                    ballSpec={ballSpec}
                    setBallSpec={setBallSpec}
                    onChange={(e) => setPlayers(players.map(p => p.id === selectedPlayerId ? { ...p, name: e.target.value } : p))}
                    ballWizardStep={ballWizardStep}
                    setBallWizardStep={setBallWizardStep}
                    // Global Settings
                    iconSize={globalSettings.iconSize}
                    showRoutes={globalSettings.showRoutes !== false}
                    showGridlines={globalSettings.showGridlines !== false}
                    autoProcessOptions={{ smooth: smoothingLevel, straighten: straightenLevel }}
                    viewMode={viewMode}
                    onBeginChange={pushHistory}
                    isLocked={isLocked}
                    profileConfig={activeProfileConfig}
                    labelMode={activeLabelMode}
                    customLabels={getCurrentPlaybook().customLabels || {}}
                    wristbandGuide={wristbandGuide}
                    onWristbandGuideChange={setWristbandGuide}
                    pickingMotionRouteId={pickingMotionRouteId}
                    setPickingMotionRouteId={setPickingMotionRouteId}
                  />
                </div>
              </div>

              <VideoPanel
                video={playVideo}
                videos={playVideos}
                activeVideoId={activeVideoId}
                playbackState={playbackState}
                mode={videoLayout}
                onModeChange={setVideoLayout}
                onSelectVideo={setActiveVideoId}
                onUpdate={updatePlayVideo}
                onAudioNoteRecorded={uploadAudioNote}
                onAudioNoteDeleted={deleteAudioNote}
                onClear={() => clearPlayVideo()}
                readOnly={isLocked}
              />

              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-50">
                {/* Wide Precision Scrubber */}
                <div className="precision-scrubber-container group">
                  {/* Progress Line */}
                  <div className="precision-scrubber-progress" style={{ width: `${playbackState.progress * 100}%` }} />
                  {/* Draggable Input */}
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.001"
                    value={playbackState.progress}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setPlaybackState(s => ({ ...s, progress: val, isPlaying: false }));
                    }}
                    className="precision-scrubber-input"
                  />
                  {/* Floating Handle */}
                  <div className="precision-scrubber-handle" style={{ left: `${playbackState.progress * 100}%` }} />

                  {/* Event Markers */}
                  {ballSpec.enabled && (() => {
                    const dynamicSegs = getDynamicBallSegments(ballSpec, players, routes, activeProfileConfig, {}, playbackState.isPlaying);
                    const duration = playbackState.duration || 10;
                    return dynamicSegs.map((seg, idx) => {
                      if (seg.kind === 'snap') {
                        return <div key={idx} className="timeline-marker snap" style={{ left: `${(seg.endTime / duration) * 100}%` }} title="Snap" />;
                      }
                      if (seg.kind === 'throw') {
                        return (
                          <React.Fragment key={idx}>
                            <div className="timeline-marker throw-start" style={{ left: `${(seg.releaseTime / duration) * 100}%` }} title="Release" />
                            <div className="timeline-marker throw-end" style={{ left: `${(seg.catchTime / duration) * 100}%` }} title="Catch" />
                          </React.Fragment>
                        );
                      }
                      if (seg.kind === 'handoff') {
                        return <div key={idx} className="timeline-marker handoff" style={{ left: `${(seg.startTime / duration) * 100}%` }} title="Handoff" />;
                      }
                      return null;
                    });
                  })()}
                </div>

                {/* Control Pill */}
                <div className="scrubber-pill px-6 py-2 rounded-full flex items-center gap-6 border border-white/10 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <button
                      className="text-white hover:text-blue-400 disabled:opacity-50 text-xl font-bold transition-colors"
                      onClick={togglePlay}
                      aria-label={playbackState.isPlaying ? 'Pause play' : 'Play play'}
                      title={playbackState.isPlaying ? 'Pause' : 'Play'}
                    >
                      {playbackState.isPlaying ? '⏸' : '▶'}
                    </button>
                    <div className="font-mono text-lg text-blue-400 min-w-[50px] text-center">
                      {((playbackState.progress * (playbackState.duration || 10))).toFixed(1)}s
                    </div>
                    {playVideos.length > 0 && (
                      <div className="qr-playbar-video-chip">
                        Video {Math.max(1, activeVideoIndex + 1)}/{playVideos.length}
                      </div>
                    )}
                  </div>

                  <div className="h-4 w-[1px] bg-white/10 mx-2" />

                  <div className="flex items-center gap-4">
                    <label className="text-[10px] text-gray-400 hover:text-white flex items-center gap-1.5 cursor-pointer uppercase font-bold tracking-wider">
                      <input
                        type="checkbox"
                        checked={playbackState.loop}
                        onChange={(e) => setPlaybackState(s => ({ ...s, loop: e.target.checked }))}
                        className="w-3 h-3 accent-blue-500 cursor-pointer"
                      />
                      Loop
                    </label>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 uppercase font-bold">Dur</span>
                      <input
                        type="number"
                        className="bg-transparent text-white text-xs w-8 text-center outline-none focus:text-blue-400 transition-colors"
                        value={playbackState.duration || 10}
                        onChange={(e) => setPlaybackState(s => ({ ...s, duration: Math.max(1, parseInt(e.target.value) || 10) }))}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 uppercase font-bold">Speed</span>
                      <select
                        className="bg-transparent text-white text-[10px] rounded cursor-pointer outline-none focus:text-blue-400 uppercase font-bold"
                        value={playbackState.speed}
                        onChange={(e) => setPlaybackState(s => ({ ...s, speed: parseFloat(e.target.value) }))}
                      >
                        <option value="0.25">0.25x</option>
                        <option value="0.5">0.5x</option>
                        <option value="1">1x</option>
                        <option value="2">2x</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </BuilderPane>
          )}
        </main>

        <aside className="qr-right-rail">
          <div className="p-4 flex flex-col gap-6 overflow-y-auto">
            {(() => {
              const p = players.find(pl => pl.id === selectedPlayerId);

              if (selectedPlayerId && p) {
                if (p.type === 'annotation') {
                  return (
                    <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Label Properties</h3>
                      <div className="flex flex-col gap-2 mb-4">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Label Text</label>
                          <textarea
                            className={`w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs h-24 ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            value={p.label || ''}
                            onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, label: e.target.value } : pl))}
                            readOnly={isLocked}
                            placeholder="Type some info about the play..."
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Color</label>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#64748b', '#000000', 'white'].map(c => (
                              <div
                                key={c}
                                className={`w-6 h-6 rounded-full cursor-pointer border-2 ${p.color === c ? 'border-white' : 'border-transparent'} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                style={{ backgroundColor: c }}
                                onClick={() => {
                                  if (isLocked) return;
                                  const idsToUpdate = selectedPlayerIds.has(p.id) ? Array.from(selectedPlayerIds) : [p.id];
                                  setPlayers(players.map(pl => idsToUpdate.includes(pl.id) ? { ...pl, color: c } : pl));
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <button
                        className={`w-full px-2 py-1.5 text-xs rounded border ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed' : 'bg-red-900 hover:bg-red-800 text-red-200 border-red-800'}`}
                        disabled={isLocked}
                        onClick={() => {
                          if (isLocked) return;
                          setPlayers(prev => prev.filter(pl => pl.id !== selectedPlayerId));
                          setRoutes(prev => prev.filter(r => r.playerId !== selectedPlayerId));
                          setSelectedPlayerId(null);
                        }}
                      >
                        Delete Label
                      </button>
                    </div>
                  );
                }
                if (p.type === 'freedraw') {
                  return (
                    <div>
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Free Draw Properties</h3>
                      <div className="flex flex-col gap-2 mb-4">
                        <div>
                          <label className="text-xs text-gray-500 block mb-1">Color</label>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', 'white'].map(c => (
                              <div
                                key={c}
                                className={`w-6 h-6 rounded-full cursor-pointer border-2 ${p.routeColor === c ? 'border-white' : 'border-transparent'} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                style={{ backgroundColor: c }}
                                onClick={() => {
                                  if (isLocked) return;
                                  setPlayers(players.map(pl => pl.id === p.id ? { ...pl, routeColor: c, color: c } : pl));
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Line End / Route Options */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1">Line End</label>
                        <select
                          value={p.lineEnd || 'Arrow'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPlayers(players.map(pl => pl.id === p.id ? { ...pl, lineEnd: val } : pl));
                            // Also update existing routes for this player
                            setRoutes(routes.map(r => {
                              if (r.playerId === p.id) {
                                return { ...r, type: val === 'Block' ? 'block' : drawingSettings.type };
                              }
                              return r;
                            }));
                          }}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                        >
                          <option value="Arrow">Arrow</option>
                          <option value="Block">Block (Flat)</option>
                        </select>
                      </div>

                      <div className="mb-2">
                        <label className="text-xs text-gray-500 block mb-1">Line Style</label>
                        <select
                          value={p.routeStyle || 'solid'}
                          onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, routeStyle: e.target.value } : pl))}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                        >
                          <option value="solid">Solid</option>
                          <option value="dashed">Dashed</option>
                          <option value="dotted">Dotted</option>
                          <option value="invisible">Invisible</option>
                        </select>
                      </div>

                      <button
                        className={`w-full px-2 py-1.5 text-xs rounded border ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed' : 'bg-red-900 hover:bg-red-800 text-red-200 border-red-800'}`}
                        disabled={isLocked}
                        onClick={() => {
                          if (isLocked) return;
                          setPlayers(prev => prev.filter(pl => pl.id !== selectedPlayerId));
                          setRoutes(prev => prev.filter(r => r.playerId !== selectedPlayerId));
                          setSelectedPlayerId(null);
                        }}
                      >
                        Delete Line
                      </button>
                    </div>
                  );
                }
                // PLAYER SELECTED VIEW
                return (
                  <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Player Properties</h3>

                    {/* Basic Props */}
                    <div className="flex flex-col gap-2 mb-4">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">
                          {activeLabelMode === 'user' ? 'Custom Label (Override)' : 'Position (Label)'}
                        </label>
                        <input
                          type="text"
                          className={`w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={(activeLabelMode === 'user' ? p.customLabel : p.label) || ''}
                          onChange={(e) => {
                            if (isLocked) return;
                            if (activeLabelMode === 'user') {
                              setPlayers(players.map(pl => pl.id === p.id ? { ...pl, customLabel: e.target.value } : pl));
                            } else {
                              setPlayers(players.map(pl => pl.id === p.id ? { ...pl, label: e.target.value } : pl));
                            }
                          }}
                          readOnly={isLocked}
                          maxLength={activeLabelMode === 'user' ? 4 : 3}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Player Name</label>
                        <input
                          type="text"
                          className={`w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={p.name || ''}
                          onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, name: e.target.value } : pl))}
                          readOnly={isLocked}
                          placeholder="e.g. Mike"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Player Color</label>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                            <div
                              key={c}
                              className={`w-6 h-6 rounded-full cursor-pointer border-2 ${p.color === c ? 'border-white' : 'border-transparent'} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                              style={{ backgroundColor: c }}
                              onClick={() => {
                                if (isLocked) return;
                                const idsToUpdate = selectedPlayerIds.has(p.id) ? Array.from(selectedPlayerIds) : [p.id];
                                setPlayers(players.map(pl => idsToUpdate.includes(pl.id) ? { ...pl, color: c } : pl));
                              }}
                            />
                          ))}
                        </div>

                        <label className="text-xs text-gray-500 block mb-1">Route Color</label>
                        <div className="flex flex-wrap gap-1">
                          {['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#64748b', '#000000', '#ffffff'].map(c => (
                            <div
                              key={c}
                              className={`w-6 h-6 rounded-full cursor-pointer border-2 ${(p.routeColor || (p.type === 'offense' ? globalSettings.offenseRouteColor : globalSettings.defenderRouteColor)) === c ? 'border-white' : 'border-transparent'} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                              style={{ backgroundColor: c }}
                              onClick={() => {
                                if (isLocked) return;
                                const idsToUpdate = selectedPlayerIds.has(p.id) ? Array.from(selectedPlayerIds) : [p.id];
                                setPlayers(players.map(pl => idsToUpdate.includes(pl.id) ? { ...pl, routeColor: c } : pl));
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <button
                      className={`w-full px-2 py-1.5 text-xs rounded border ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed' : 'bg-red-900 hover:bg-red-800 text-red-200 border-red-800'}`}
                      disabled={isLocked}
                      onClick={() => {
                        if (isLocked) return;
                        setPlayers(prev => prev.filter(pl => pl.id !== selectedPlayerId));
                        setRoutes(prev => prev.filter(r => r.playerId !== selectedPlayerId));
                        setSelectedPlayerId(null);
                      }}
                    >
                      Delete Player
                    </button>

                    <div className="mt-4 border-t border-gray-700 pt-4">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Icon Settings</h3>
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1">Shape</label>
                        <select
                          value={p.shape || 'Circle'}
                          onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, shape: e.target.value } : pl))}
                          disabled={isLocked}
                          className={`w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <option value="Circle">Circle</option>
                          <option value="Square">Square</option>
                          <option value="Triangle">Triangle</option>
                          <option value="X">X (Block)</option>
                        </select>
                      </div>

                      {/* Line End / Route Options */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1">Line End</label>
                        <select
                          value={p.lineEnd || 'Arrow'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPlayers(players.map(pl => pl.id === p.id ? { ...pl, lineEnd: val } : pl));
                            // Also update existing routes for this player
                            setRoutes(routes.map(r => {
                              if (r.playerId === p.id) {
                                return { ...r, type: val === 'Block' ? 'block' : drawingSettings.type };
                              }
                              return r;
                            }));
                          }}
                          className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                        >
                          <option value="Arrow">Arrow</option>
                          <option value="Block">Block (Flat)</option>
                        </select>
                      </div>

                      {/* Speed Slider */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1 flex justify-between items-center">
                          <span>Speed (40y)</span>
                          <input
                            type="number"
                            min="4.2" max="9.0" step="0.1"
                            className="w-12 bg-gray-800 border border-gray-700 rounded text-xs text-white text-center"
                            value={p.speed || getDefaultPlayerSpeed(p.type, p.role)}
                            onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, speed: parseFloat(e.target.value) } : pl))}
                            readOnly={isLocked}
                            disabled={isLocked}
                          />
                        </label>
                        <input
                          type="range"
                          min="4.2" max="9.0" step="0.1"
                          className="w-full accent-blue-500"
                          value={p.speed || getDefaultPlayerSpeed(p.type, p.role)}
                          onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, speed: parseFloat(e.target.value) } : pl))}
                          disabled={isLocked}
                        />
                      </div>

                      {/* Route Delay */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1 flex justify-between items-center">
                          <span>Route Delay (sec)</span>
                          <input
                            type="number"
                            min="0" max="5.0" step="0.1"
                            className="w-12 bg-gray-800 border border-gray-700 rounded text-xs text-white text-center"
                            value={p.routeDelay || 0}
                            onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, routeDelay: parseFloat(e.target.value) } : pl))}
                            readOnly={isLocked}
                            disabled={isLocked}
                          />
                        </label>
                        <input
                          type="range"
                          min="0" max="5.0" step="0.1"
                          className="w-full accent-orange-500"
                          value={p.routeDelay || 0}
                          onChange={(e) => !isLocked && setPlayers(players.map(pl => pl.id === p.id ? { ...pl, routeDelay: parseFloat(e.target.value) } : pl))}
                          disabled={isLocked}
                        />
                      </div>

                      {/* Handoff Delay */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1 flex justify-between items-center">
                          <span>Handoff Delay (sec)</span>
                          <input
                            type="number"
                            min="0" max="5.0" step="0.1"
                            className="w-12 bg-gray-800 border border-gray-700 rounded text-xs text-white text-center"
                            value={p.handoffDelay || 0}
                            onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, handoffDelay: parseFloat(e.target.value) } : pl))}
                          />
                        </label>
                        <input
                          type="range"
                          min="0" max="5.0" step="0.1"
                          className="w-full accent-blue-500"
                          value={p.handoffDelay || 0}
                          onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, handoffDelay: parseFloat(e.target.value) } : pl))}
                        />
                      </div>

                      {/* Throw Delay */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 block mb-1 flex justify-between items-center">
                          <span>Throw Delay (sec)</span>
                          <input
                            type="number"
                            min="0" max="5.0" step="0.1"
                            className="w-12 bg-gray-800 border border-gray-700 rounded text-xs text-white text-center"
                            value={p.throwDelay || 0}
                            onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, throwDelay: parseFloat(e.target.value) } : pl))}
                          />
                        </label>
                        <input
                          type="range"
                          min="0" max="5.0" step="0.1"
                          className="w-full accent-green-500"
                          value={p.throwDelay || 0}
                          onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, throwDelay: parseFloat(e.target.value) } : pl))}
                        />
                      </div>

                      {/* Zone Settings */}
                      <div className="mb-2 flex justify-between items-center">
                        <label className="flex items-center gap-2 text-xs text-gray-300">
                          <input
                            type="checkbox"
                            checked={!!p.zoneParams?.enabled}
                            onChange={(e) => setPlayers(players.map(pl => {
                              if (pl.id !== p.id) return pl;
                              const enabled = e.target.checked;
                              const zp = pl.zoneParams || {};
                              // If enabling and relative is undefined, default to Static (relative: false)
                              // and snapshot current position so it doesn't "follow" by default fallback.
                              if (enabled && zp.relative === undefined) {
                                return {
                                  ...pl,
                                  zoneParams: {
                                    ...zp,
                                    enabled: true,
                                    relative: false,
                                    x: pl.x,
                                    y: pl.y
                                  }
                                };
                              }
                              return { ...pl, zoneParams: { ...zp, enabled } };
                            }))}
                          />
                          Show Zone
                        </label>
                        {p.zoneParams?.enabled && (
                          <button
                            className="text-[10px] text-blue-400 hover:text-blue-300 uppercase font-bold"
                            onClick={() => setPlayers(players.map(pl => {
                              if (pl.id !== p.id) return pl;
                              const isRel = !!pl.zoneParams?.relative;
                              return {
                                ...pl,
                                zoneParams: {
                                  ...pl.zoneParams,
                                  x: isRel ? 0 : pl.x,
                                  y: isRel ? 0 : pl.y
                                }
                              };
                            }))}
                          >
                            Reset Zone
                          </button>
                        )}
                      </div>
                      {
                        p.zoneParams?.enabled && (
                          <div className="pl-4 border-l-2 border-gray-700 space-y-2 mb-2">
                            <label className="flex items-center gap-2 text-[10px] text-gray-400 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!p.zoneParams?.relative}
                                onChange={(e) => {
                                  const isRel = e.target.checked;
                                  setPlayers(players.map(pl => {
                                    if (pl.id !== p.id) return pl;
                                    const zp = pl.zoneParams || {};
                                    const oldRel = !!zp.relative; // Default false
                                    if (oldRel === isRel) return pl;

                                    // Coordinate conversion
                                    let newX = zp.x || 0;
                                    let newY = zp.y || 0;
                                    if (isRel) {
                                      // Abs -> Rel
                                      newX = (zp.x ?? pl.x) - pl.x;
                                      newY = (zp.y ?? pl.y) - pl.y;
                                    } else {
                                      // Rel -> Abs
                                      newX = pl.x + (zp.x || 0);
                                      newY = pl.y + (zp.y || 0);
                                    }

                                    return { ...pl, zoneParams: { ...zp, relative: isRel, x: newX, y: newY } };
                                  }));
                                }}
                              />
                              Follows Player
                            </label>
                            <div>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <label className="text-[10px] text-gray-500 block">Width (yds)</label>
                                  <input type="range" min="2" max="30" step="1"
                                    value={p.zoneParams?.w || 8}
                                    onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, zoneParams: { ...pl.zoneParams, w: parseFloat(e.target.value) } } : pl))}
                                    className="w-full h-1 bg-gray-600 rounded"
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="text-[10px] text-gray-500 block">Height (yds)</label>
                                  <input type="range" min="2" max="30" step="1"
                                    value={p.zoneParams?.h || 6}
                                    onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, zoneParams: { ...pl.zoneParams, h: parseFloat(e.target.value) } } : pl))}
                                    className="w-full h-1 bg-gray-600 rounded"
                                  />
                                </div>
                              </div>
                            </div>
                            <div>
                              <label className="text-[10px] text-gray-500 block">Opacity</label>
                              <input type="range" min="0.1" max="1" step="0.1"
                                value={p.zoneParams?.alpha || 0.3}
                                onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, zoneParams: { ...pl.zoneParams, alpha: parseFloat(e.target.value) } } : pl))}
                                className="w-full h-1 bg-gray-600 rounded"
                              />
                            </div>
                          </div>
                        )
                      }

                      {/* Route Options */}
                      <div className="mb-2 pt-2 border-t border-gray-700">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Route Options</label>

                        <div className="flex flex-col gap-1 mb-2">
                          <label className="flex items-center gap-2 text-xs text-gray-300">
                            <input
                              type="checkbox"
                              checked={!!p.hideRouteDuringPlayback}
                              onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, hideRouteDuringPlayback: e.target.checked } : pl))}
                            />
                            Hide Route During Play
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-300">
                            <input
                              type="checkbox"
                              checked={!!p.visualOnly}
                              onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, visualOnly: e.target.checked } : pl))}
                            />
                            Visual Only (Static)
                          </label>
                        </div>

                        <div className="mb-2">
                          <label className="text-xs text-gray-500 block mb-1">Line Style</label>
                          <select
                            value={p.routeStyle || 'solid'}
                            onChange={(e) => setPlayers(players.map(pl => pl.id === p.id ? { ...pl, routeStyle: e.target.value } : pl))}
                            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="invisible">Invisible</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // NO PLAYER SELECTED
                return (
                  <div className="text-center py-8 text-gray-500 text-xs">
                    Select a player to edit properties.<br />
                    <span className="text-[10px] text-gray-600 mt-2 block">Or use Global Stylings on the left rail</span>
                  </div>
                );
              }
            })()}

            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Play Settings</h3>
              <div className="mb-2"><label className="text-xs text-gray-500 block mb-1">Play Name</label><input type="text" className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs" value={playName} onChange={(e) => setPlayName(e.target.value)} /></div>
              <div className="mb-2"><label className="text-xs text-gray-500 block mb-1">Formation Name</label><div className="text-xs text-gray-300 bg-gray-800 px-2 py-1 rounded border border-gray-700">{currentFormation}</div></div>
            </div>
            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Play Video</h3>

              <div className="flex flex-col gap-3 text-xs">
                <label className={`w-full px-2 py-2 text-xs rounded border text-center ${isLocked || !isCloud ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed' : 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-100 border-blue-800 cursor-pointer'}`}>
                  {playVideos.length ? 'Upload & Attach More' : 'Upload & Attach Video'}
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    className="hidden"
                    disabled={isLocked || !isCloud}
                    onChange={(e) => {
                      uploadHostedVideo(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
                {!isCloud && <div className="text-[10px] text-gray-500">Uploads require a cloud playbook.</div>}
                {videoUploadStatus && <div className="text-[10px] text-blue-300">{videoUploadStatus}</div>}

                {videoLibrary.length > 0 && (
                  <div className="qr-video-library-picker">
                    <div className="qr-video-library-title">Video Library</div>
                    <div className="qr-video-rail-list">
                      {videoLibrary.map((video, index) => {
                        const alreadyAttached = playVideos.some(attached => (
                          attached.libraryVideoId === video.id ||
                          (attached.storagePath && attached.storagePath === video.storagePath)
                        ));
                        return (
                          <button
                            key={video.id}
                            type="button"
                            className={`qr-video-rail-item ${alreadyAttached ? 'attached' : ''}`}
                            onClick={() => attachVideoFromLibrary(video)}
                            title={video.fileName || video.label}
                          >
                            <span>{index + 1}</span>
                            <strong>{video.label || video.fileName || `Video ${index + 1}`}</strong>
                            <em>{alreadyAttached ? 'Attached' : 'Attach'}</em>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {playVideos.length > 0 && (
                  <div className="qr-video-rail-list">
                    {playVideos.map((video, index) => (
                      <button
                        key={video.id}
                        type="button"
                        className={`qr-video-rail-item ${video.id === playVideo?.id ? 'active' : ''}`}
                        onClick={() => setActiveVideoId(video.id)}
                        title={video.fileName || video.label}
                      >
                        <span>{index + 1}</span>
                        <strong>{video.label || video.fileName || `Video ${index + 1}`}</strong>
                      </button>
                    ))}
                  </div>
                )}

                {playVideo ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 font-bold">Video {Math.max(1, activeVideoIndex + 1)} of {playVideos.length}</span>
                      <button
                        className="text-gray-400 hover:text-white border border-gray-700 rounded px-2 py-1"
                        onClick={() => setVideoLayout(videoLayout === 'side' ? 'floating' : 'side')}
                      >
                        {videoLayout === 'side' ? 'Float' : 'Side-by-side'}
                      </button>
                    </div>

                    {playVideo.type === 'upload' ? (
                      <>
                        <label className="text-gray-500 block">
                          Video Label
                          <input
                            readOnly={isLocked}
                            className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                            value={playVideo.label || ''}
                            onChange={(e) => updatePlayVideo({ label: e.target.value })}
                          />
                        </label>
                        <div className="text-gray-400 truncate" title={playVideo.fileName}>{playVideo.fileName}</div>
                        <label className="text-gray-500 block">
                          Start video at play time
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              readOnly={isLocked}
                              type="number"
                              min="0"
                              step="0.5"
                              className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                              value={playVideo.playStartAt || 0}
                              onChange={(e) => updatePlayVideo({ playStartAt: toSeconds(e.target.value) })}
                            />
                            <span className="text-gray-500">seconds</span>
                          </div>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-gray-500 block">
                            Trim In
                            <input
                              readOnly={isLocked}
                              type="number"
                              min="0"
                              step="0.5"
                              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                              value={playVideo.trimStart || 0}
                              onChange={(e) => updatePlayVideo({ trimStart: toSeconds(e.target.value) })}
                            />
                          </label>
                          <label className="text-gray-500 block">
                            Trim Out
                            <input
                              readOnly={isLocked}
                              type="number"
                              min="0"
                              step="0.5"
                              className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs"
                              value={playVideo.trimEnd || 0}
                              onChange={(e) => updatePlayVideo({ trimEnd: toSeconds(e.target.value) })}
                            />
                          </label>
                        </div>
                        <button
                          disabled={isLocked}
                          className={`w-full px-2 py-1.5 rounded border ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-100 border-blue-800'}`}
                          onClick={saveVideoTrimAsNewPlay}
                        >
                          Save Trim As New Play
                        </button>
                      </>
                    ) : (
                      <div className="text-gray-400">
                        YouTube attachments are disabled while hosted video sync is being finished. Detach this video and upload a hosted file.
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-2">
                      <button
                        disabled={isLocked}
                        className={`px-2 py-1.5 rounded border ${isLocked ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700'}`}
                        onClick={() => clearPlayVideo()}
                      >
                        Remove from Play
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] text-gray-500">Add video clips to compare different angles or reps of this play.</div>
                )}
              </div>
            </div>
            <div className="border-t border-gray-700 pt-4 flex-1 min-h-0 flex flex-col">
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-bold text-gray-500">Quick Formations</label>
                <div className="flex gap-1">
                  <button
                    className="text-[10px] bg-blue-700 hover:bg-blue-600 px-2 py-0.5 rounded text-white transition-colors"
                    onClick={() => { const name = prompt('Save formation name:', selectedFormation || 'New Formation'); if (name) saveFormation(name); }}
                    title="Save current player positions as a NEW formation template"
                  >
                    Save New
                  </button>
                  {selectedFormation && (
                    <button
                      className="text-[10px] bg-green-700 hover:bg-green-600 px-2 py-0.5 rounded text-white font-bold transition-colors"
                      onClick={() => { if (window.confirm(`Update stored layout for "${selectedFormation}" with current screen positions?`)) saveFormation(selectedFormation); }}
                      title="Update 'selectedFormation' layout"
                    >
                      Update
                    </button>
                  )}
                  {selectedFormation && (
                    <button
                      className="text-[10px] bg-gray-600 hover:bg-gray-500 px-2 py-0.5 rounded text-white transition-colors"
                      onClick={() => restoreFormationToDefault(selectedFormation)}
                      title="Discard layout changes and reload default/last-saved layout"
                    >
                      Reset Layout
                    </button>
                  )}
                </div>
              </div>
              <div className="overflow-y-auto bg-gray-900 border border-gray-700 rounded p-1 flex-1 max-h-40">
                {(() => {
                  const pb = getCurrentPlaybook();
                  const config = pb.profileConfig;
                  const setKey = config?.formationSetKey || 'flag_6v6';
                  const formationsMap = FORMATIONS_BY_FORMAT[setKey] || FORMATIONS_BY_FORMAT['flag_6v6'];
                  const sideKey = teamType.toLowerCase() === 'defense' ? 'defense' : 'offense';
                  const presets = formationsMap[sideKey] || {};

                  // Combine custom and presets, filtering for valid ones
                  const customKeys = Object.keys(pb.formations || {});
                  const presetKeys = Object.keys(presets);
                  const allKeys = Array.from(new Set([...customKeys, ...presetKeys]));

                  return allKeys.map(f => (
                    <div
                      key={f}
                      className={`flex justify-between items-center px-2 py-1.5 hover:bg-gray-800 cursor-pointer text-[11px] rounded mb-0.5 transition-all ${selectedFormation === f ? 'bg-blue-600/20 text-blue-200 border-l-2 border-l-blue-500 pl-1.5' : 'text-gray-400 border-l-2 border-l-transparent'}`}
                      onClick={() => loadFormation(f)}
                    >
                      <span className="truncate flex-1" title={f}>{f}</span>
                      <div className="flex items-center gap-2">
                        {pb.formations?.[f] && <span className="text-[9px] text-gray-600 italic">Custom</span>}
                        {pb.formations?.[f] && (
                          <button
                            className="text-red-400 hover:text-red-300 w-4 h-4 flex items-center justify-center rounded hover:bg-red-900/40"
                            onClick={(e) => { e.stopPropagation(); deleteFormation(f); }}
                            title="Delete custom formation"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </aside >
      </div >

      {/* VERSION MODAL */}
      {
        showVersionModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]" onClick={() => setShowVersionModal(false)}>
            <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-2xl p-6 w-[500px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
                <h2 className="text-xl font-bold text-white">Changelog</h2>
                <button onClick={() => setShowVersionModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              {changelog.map((log, i) => (
                <div key={i} className="mb-6">
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="text-lg font-bold text-blue-400">v{log.version}</h3>
                    <span className="text-xs text-gray-500">{log.date}</span>
                  </div>
                  {log.features && (
                    <div className="mb-2">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">New Features</h4>
                      <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                        {log.features.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                  {log.fixes && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bug Fixes</h4>
                      <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                        {log.fixes.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      }

      <div className="fixed bottom-2 right-2 z-50">
        <button
          onClick={() => setShowVersionModal(true)}
          className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors bg-gray-900/50 px-2 py-1 rounded">
          v{currentVersion}
        </button>
      </div>

      {
        contextMenu && (() => {
          const target = players.find(p => p.id === contextMenu.targetId);
          const isBottom = contextMenu.y > window.innerHeight * 0.6;
          const menuStyle = { left: contextMenu.x, top: isBottom ? 'auto' : contextMenu.y, bottom: isBottom ? (window.innerHeight - contextMenu.y) : 'auto' };

          if (contextMenu.type === 'route' || contextMenu.type === 'route-endpoint') {
            const route = routes.find(r => r.id === contextMenu.targetId);
            return (
              <div className="fixed bg-gray-900 border border-gray-700 rounded shadow-2xl py-1 z-50 text-xs w-52 overflow-hidden" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                <div className="px-3 py-2 bg-gray-800 text-blue-400 font-bold border-b border-gray-700 mb-1 flex items-center gap-2">
                  <span>⚡</span> {contextMenu.type === 'route-endpoint' ? 'Endpoint Options' : 'Route Options'}
                </div>

                <button
                  className="w-full text-left px-3 py-2 hover:bg-blue-600 text-white flex items-center gap-2 transition-colors"
                  onClick={() => {
                    setPickingMotionRouteId(contextMenu.targetId);
                    setContextMenu(null);
                  }}
                >
                  <span className="text-base">📍</span> Set Pre-Snap Motion Point
                </button>

                <button
                  className="w-full text-left px-3 py-2 hover:bg-amber-600 text-white flex items-center gap-2 transition-colors border-t border-gray-700 font-bold"
                  onClick={() => {
                    if (route) {
                      const lastPoint = route.pointsAbs[route.pointsAbs.length - 1];
                      const owner = players.find(p => p.id === route.playerId);
                      const anchorPos = { x: owner.x, y: owner.y };
                      const dx = anchorPos.x - (route.drawBase?.x ?? route.pointsAbs[0].x);
                      const dy = anchorPos.y - (route.drawBase?.y ?? route.pointsAbs[0].y);

                      const startPos = { x: lastPoint.x + dx, y: lastPoint.y + dy };

                      setSelectedTool('draw');
                      setSelectedPlayerId(route.playerId);
                      setSelectedPlayerIds(new Set([route.playerId]));
                      setDrawingSettings(prev => ({ ...prev, type: 'option', startPos }));
                    }
                    setContextMenu(null);
                  }}
                >
                  <span className="text-lg">🔀</span> Add Option Route
                </button>

                {route?.motionU !== undefined && route?.motionU !== null && (
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-red-900/40 text-red-400 flex items-center gap-2 transition-colors border-t border-gray-700"
                    onClick={() => {
                      pushHistory();
                      setRoutes(routes.map(r => r.id === route.id ? { ...r, motionU: null } : r));
                      setContextMenu(null);
                    }}
                  >
                    <span className="text-base">🗑️</span> Clear Motion Point
                  </button>
                )}

                <button
                  className="w-full text-left px-3 py-2 hover:bg-red-900/40 text-red-400 flex items-center gap-2 transition-colors border-t border-gray-700"
                  onClick={() => {
                    pushHistory();
                    setRoutes(routes.filter(r => r.id !== route.id));
                    setContextMenu(null);
                  }}
                >
                  <span className="text-base">🗑️</span> Delete This Route
                </button>

                <div className="border-t border-gray-700 mt-1">
                  <button className="w-full text-left px-3 py-2 text-gray-500 hover:text-gray-300" onClick={() => setContextMenu(null)}>Close</button>
                </div>
              </div>
            );
          }

          if (contextMenu.type === 'background') {
            return (
              <FieldContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                pos={contextMenu.pos}
                selectedCount={selectedPlayerIds.size}
                onAction={(action, pos) => {
                  if (action === 'check-walkthrough') return globalSettings.walkthroughMode;

                  // Allow adding players in walkthrough mode (they become ghost players)
                  const isAddingPlayer = ['add-offense', 'add-defense', 'add-ghost-defense'].includes(action);
                  if (isLocked && !isAddingPlayer) return;

                  if (action === 'add-offense' || action === 'add-defense' || action === 'add-ghost-defense') {
                    const type = action === 'add-offense' ? 'offense' : 'defense';
                    const isWalkthrough = globalSettings.walkthroughMode || action === 'add-ghost-defense';

                    if (!isWalkthrough) {
                      handleAddPlayerAt(type, pos);
                    } else {
                      const id = Date.now();
                      const newPlayer = {
                        id,
                        type,
                        x: pos.x,
                        y: pos.y,
                        label: type === 'offense' ? 'P' : 'X',
                        role: type === 'offense' ? 'WR' : 'DEF',
                        speed: 5.5,
                        color: type === 'offense' ? globalSettings.offenseColor : globalSettings.defenderColor,
                        routeColor: type === 'offense' ? globalSettings.offenseRouteColor : globalSettings.defenderRouteColor,
                        isWalkthrough: true
                      };
                      setPlayers(prev => [...prev, newPlayer]);
                      setSelectedPlayerId(id);
                      setSelectedPlayerIds(new Set([id]));
                    }
                    setContextMenu(null);
                    return;
                  }
                  if (action === 'add-blitz') handleAddMarkerAt('blitz', pos);
                  if (action === 'add-firstdown') handleAddMarkerAt('firstdown', pos);
                  if (action === 'add-linemarker') handleAddMarkerAt('line-marker', pos);
                  if (action === 'add-los') handleAddMarkerAt('los', pos);
                  if (action === 'add-cone') handleAddMarkerAt('cone', pos);
                  if (action === 'add-label') handleAddMarkerAt('annotation', pos);
                  if (action === 'add-freedraw') handleAddMarkerAt('freedraw', pos);
                  if (action === 'add-fake-handoff') handleAddMarkerAt('fake-handoff', pos);
                  if (action.startsWith('straight-') || action.startsWith('space-')) handleArrange(action);
                }}
                onClose={() => setContextMenu(null)}
              />
            );
          }

          if (target && target.type === 'ball') {
            return (
              <div className="fixed bg-gray-800 border border-gray-700 rounded shadow-xl py-1 z-50 text-xs w-48" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                <div className="px-3 py-1 text-gray-500 font-bold border-b border-gray-700 mb-1">Ball Options</div>
                <div className="px-3 py-2 text-gray-400 italic whitespace-normal border-b border-gray-700 mb-1">{describeBallChain(ballSpec, players)}</div>
                <button
                  className="w-full text-left px-3 py-1 hover:bg-blue-900 text-blue-300"
                  onClick={() => {
                    setSelectedTool('ball');
                    setBallWizardStep(1);
                    setContextMenu(null);
                  }}
                >
                  Edit Ball Movement
                </button>
                <button className="w-full text-left px-3 py-1 hover:bg-red-900 text-red-300" onClick={() => { if (isLocked) return; setPlayers(players.filter(p => p.id !== target.id)); setBallSpec({ ...initialBallSpec }); setContextMenu(null); }}>Remove Ball</button>
                <button className="w-full text-left px-3 py-1 text-gray-400 mt-2" onClick={() => setContextMenu(null)}>Close</button>
              </div>
            );
          }
          if (target && ['blitz', 'firstdown', 'line-marker', 'cone'].includes(target.type)) {
            const isGuide = ['blitz', 'firstdown', 'line-marker'].includes(target.type);
            const markerTitle = target.type === 'blitz' ? 'Blitz Marker' : (target.type === 'firstdown' ? 'First Down Marker' : (target.type === 'cone' ? 'Cone' : 'Field Line'));
            const colors = ['yellow', 'red', 'blue', 'white', '#3be2ff', '#fbbf24', '#ef4444'];

            return (
              <div className="fixed bg-gray-800 border border-gray-700 rounded shadow-xl py-1 z-50 text-xs w-48" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                <div className="px-3 py-1 text-gray-500 font-bold border-b border-gray-700 mb-1">{markerTitle}</div>

                {isGuide && (
                  <>
                    <button
                      className="w-full text-left px-3 py-1 hover:bg-blue-900 text-blue-300 flex items-center gap-2"
                      onClick={() => {
                        pushHistory();
                        setPlayers(players.map(p => p.id === target.id ? { ...p, showBlitzLine: !p.showBlitzLine } : p));
                        setContextMenu(null);
                      }}
                    >
                      {target.showBlitzLine ? '✕ Hide Full-Field Line' : '➕ Show Full-Field Line'}
                    </button>

                    {target.type === 'line-marker' && (
                      <button
                        className="w-full text-left px-3 py-1 hover:bg-purple-900 text-purple-300 flex items-center gap-2"
                        onClick={() => {
                          pushHistory();
                          setPlayers(players.map(p => p.id === target.id ? { ...p, lineStyle: p.lineStyle === 'dotted' ? 'solid' : 'dotted' } : p));
                          setContextMenu(null);
                        }}
                      >
                        {target.lineStyle === 'dotted' ? '--- Style: Solid' : '... Style: Dotted'}
                      </button>
                    )}

                    <div className="px-3 py-1 text-gray-500 border-t border-gray-700 mt-1">Line Color</div>
                    <div className="flex flex-wrap gap-1 px-3 py-1 mb-1">
                      {colors.map(c => (
                        <div
                          key={c}
                          className={`w-4 h-4 rounded-full cursor-pointer border ${(target.color || (target.type === 'blitz' ? '#ef4444' : '#fbbf24')) === c ? 'border-white' : 'border-transparent'}`}
                          style={{ backgroundColor: c }}
                          onClick={() => {
                            pushHistory();
                            setPlayers(players.map(p => p.id === target.id ? { ...p, color: c } : p));
                            setContextMenu(null);
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}

                <button className="w-full text-left px-3 py-1 hover:bg-red-900 text-red-300 border-t border-gray-700" onClick={() => { if (isLocked) return; setPlayers(players.filter(p => p.id !== target.id)); setContextMenu(null); }}>Remove Marker</button>
                <button className="w-full text-left px-3 py-1 text-gray-400 mt-2" onClick={() => setContextMenu(null)}>Close</button>
              </div>
            );
          }
          if (target) {
            return (
              <PlayerMenu
                target={target}
                routes={routes}
                style={menuStyle}
                profileConfig={activeProfileConfig}
                onClose={() => setContextMenu(null)}
                onClonePlayer={handleClonePlayer}
                onSetMotion={(rid) => { setPickingMotionRouteId(rid); setContextMenu(null); }}
                onDeleteRoute={() => { if (isLocked && !globalSettings.walkthroughMode) return; pushHistory(); setRoutes(routes.filter(r => r.playerId !== contextMenu.targetId)); setContextMenu(null); }}
                onApplyZone={(zoneData) => {
                  console.log("Applying zone:", zoneData);
                  pushHistory();
                  // Default relative is false (Static)
                  const newZoneParams = { ...target.zoneParams, enabled: true, relative: false, ...zoneData };

                  // If we want to start perfectly centered, we might need to set x/y to 0 if relative, 
                  // or to target.x/target.y if static.
                  // Since we are applying a preset, let's assume centered on player at current moment.
                  if (!newZoneParams.relative) {
                    newZoneParams.x = target.x;
                    newZoneParams.y = target.y;
                  } else {
                    newZoneParams.x = 0;
                    newZoneParams.y = 0;
                  }

                  setPlayers(players.map(pl => pl.id === target.id ? { ...pl, zoneParams: newZoneParams } : pl));
                  setContextMenu(null);
                }}
                onSelectRoute={(routeFn, category) => {
                  if (typeof routeFn !== 'function') return;
                  pushHistory();
                  const routePath = routeFn();
                  const points = [{ x: target.x, y: target.y }, ...routePath];
                  const isBlocking = category === 'Blocking';
                  const newRoute = {
                    id: Date.now(),
                    playerId: target.id,
                    mode: 'absolute-anchored',
                    pointsAbs: points,
                    drawBase: { x: target.x, y: target.y },
                    color: target.routeColor || (target.type === 'offense' ? globalSettings.offenseRouteColor : globalSettings.defenderRouteColor) || drawingSettings.color,
                    type: (isBlocking || target.lineEnd === 'Block') ? 'block' : drawingSettings.type,
                    isPreset: true, // Flag as preset so smoothing tool skips it
                    isStraight: true,
                    isFreeHand: false // Structured
                  };
                  setRoutes(prev => {
                    const isNewOption = !isBlocking && target.lineEnd !== 'Block' && drawingSettings.type === 'option';
                    const others = prev.filter(r => {
                      if (r.playerId !== target.id) return true;
                      if (isNewOption) return r.type !== 'option';
                      return r.type === 'option';
                    });
                    return [...others, newRoute];
                  });
                  setContextMenu(null);
                }}
                isLocked={isLocked && !globalSettings.walkthroughMode}
                onFreeDraw={(mode = 'solid', startPos = null) => {
                  if (isLocked && !globalSettings.walkthroughMode) return;
                  // mode: 'solid' or 'option'
                  // startPos: if provided, start drawing from there
                  setSelectedTool('draw');
                  setSelectedPlayerId(contextMenu.targetId);
                  setSelectedPlayerIds(new Set([contextMenu.targetId]));
                  // IMPORTANT: explicitly reset/set based on mode
                  setDrawingSettings(prev => ({ ...prev, type: mode, startPos }));
                  setContextMenu(null);
                }}
              />
            );
          }
          return null;
        })()
      }

      {
        showShareModal && (
          <ShareModal
            playbook={activePbData}
            currentPlayId={(() => {
              const pb = getCurrentPlaybook();
              const allPlaysMap = pb.plays || {};
              return Object.keys(allPlaysMap).find(id => allPlaysMap[id].playName === playName || id === playName);
            })()}
            onInvite={(email) => inviteUserByEmail(playbookId, email)}
            onRemove={(uid) => removeCollaborator(playbookId, uid)}
            onSetPublic={(isPublic) => setPlaybookPublic(playbookId, isPublic)}
            onClose={() => setShowShareModal(false)}
          />
        )
      }

      {
        showVideoLibraryModal && (
          <div className="qr-video-library-modal" onClick={closeVideoLibraryModule}>
            <section className="qr-video-library-dialog" onClick={(e) => e.stopPropagation()}>
              <header className="qr-video-library-header">
                <div>
                  <h2>Video Library</h2>
                  <p>{isCloud ? `${videoLibrary.length} stored video${videoLibrary.length === 1 ? '' : 's'} in this playbook` : 'Video library is available in cloud playbooks.'}</p>
                </div>
                <button type="button" onClick={closeVideoLibraryModule} title="Close video library">x</button>
              </header>

              <div className="qr-video-library-upload">
                <label className={isLocked || !isCloud || !canEdit ? 'disabled' : ''}>
                  Upload Videos
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    disabled={isLocked || !isCloud}
                    onChange={(e) => {
                      uploadHostedVideo(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </label>
                <span>Up to 50 MB per video. Uploads are stored in the playbook library, then attached to this play.</span>
              </div>

              {videoUploadStatus && <div className="qr-video-library-status">{videoUploadStatus}</div>}

              <div className="qr-video-library-grid">
                {videoLibrary.length === 0 ? (
                  <div className="qr-video-library-empty">
                    <strong>No stored videos yet.</strong>
                    <span>Upload clips here, then attach them to any play in this playbook.</span>
                  </div>
                ) : (
                  videoLibrary.map((video, index) => {
                    const attached = playVideos.some(item => (
                      item.libraryVideoId === video.id ||
                      (item.storagePath && item.storagePath === video.storagePath)
                    ));
                    return (
                      <article key={video.id} className={`qr-video-library-card ${attached ? 'attached' : ''}`}>
                        <div className="qr-video-library-card-index">{index + 1}</div>
                        <div className="qr-video-library-card-body">
                          <strong title={video.fileName || video.label}>{video.label || video.fileName || `Video ${index + 1}`}</strong>
                          <span>{video.fileName || 'Hosted video'}</span>
                          {video.sizeBytes ? <small>{(video.sizeBytes / (1024 * 1024)).toFixed(1)} MB</small> : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => attachVideoFromLibrary(video)}
                  disabled={attached || isLocked || !canEdit}
                        >
                          {attached ? 'Attached' : 'Attach'}
                        </button>
                      </article>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )
      }

      {
        isPrintModalOpen && (
          <PrintModal
            isOpen={isPrintModalOpen}
            onClose={() => setIsPrintModalOpen(false)}
            allPlays={isCloud ? cloudPlays : (playbookData[teamType]?.playbooks[currentPlaybook]?.plays || {})}
            playbook={getCurrentPlaybook()}
            activePlay={{ playName, players, routes, ballSpec, formation: currentFormation }}
            playbookId={playbookId}
            teamType={teamType}
            currentPlaybook={currentPlaybook}
            theme={globalSettings.theme}
            globalSettings={globalSettings}
            wristbandGuide={wristbandGuide}
          />
        )
      }

      {
        isRosterModalOpen && (
          <RosterModal
            isOpen={isRosterModalOpen}
            onClose={() => setIsRosterModalOpen(false)}
          />
        )
      }

      {/* HELP OVERLAY */}
      {
        showHelpOverlay && (
          <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-6 backdrop-blur-sm" onClick={() => setShowHelpOverlay(false)}>
            <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-2xl w-full p-8 text-white relative animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
              <button className="absolute top-4 right-4 text-gray-500 hover:text-white text-2xl" onClick={() => setShowHelpOverlay(false)}>×</button>
              <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
                <span className="text-blue-500">⌨️</span> Keyboard Shortcuts
              </h2>
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 text-sm">
                {[
                  { key: 'Space', desc: 'Play / Stop Animation' },
                  { key: 'Ctrl + Z', desc: 'Undo' },
                  { key: 'Ctrl + Y', desc: 'Redo' },
                  { key: 'Del / Backspace', desc: 'Delete Selected Player' },
                  { key: '?', desc: 'Show / Hide this help' },
                  { key: 'S', desc: 'Select Tool' },
                  { key: 'D', desc: 'Draw Route' },
                  { key: 'Arrow Keys', desc: 'Pan Field' },
                  { key: 'Shift + Drag', desc: 'Add Player to Selection' },
                  { key: 'Shift (Drawing)', desc: 'Snap to 45 degree angles' },
                ].map(item => (
                  <div key={item.key} className="flex justify-between items-center border-b border-gray-800 pb-2">
                    <span className="text-gray-400 font-medium">{item.desc}</span>
                    <kbd className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs font-mono text-blue-400">{item.key}</kbd>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }

      {/* TOAST & STATUS CONTAINER */}
      <div className="fixed bottom-6 right-6 z-[10000] flex flex-col gap-2 pointer-events-none items-end">
        {/* Persistent Sync Status */}
        <div className="bg-gray-900/40 backdrop-blur-sm border border-gray-700/50 px-3 py-1.5 rounded-lg mb-2 flex items-center gap-2 shadow-sm pointer-events-auto">
          {saveStatus === 'saving' && <span className="text-yellow-500 animate-pulse text-[10px] font-black uppercase tracking-tighter">● Syncing...</span>}
          {saveStatus === 'saved' && (
            <span className={`${isCloud ? 'text-green-500' : 'text-blue-400'} text-[10px] font-black uppercase tracking-tighter flex items-center gap-1.5`}>
              <span className="text-lg">✓</span> {isCloud ? 'Cloud Synced' : 'Locally Saved'}
            </span>
          )}
          {saveStatus === 'error' && (
            <button
              onClick={() => savePlay(playName, selectedFormation)}
              className="text-red-500 hover:text-red-400 font-black text-[10px] uppercase tracking-tighter flex items-center gap-1.5"
            >
              × Sync Error (Retry)
            </button>
          )}
          {saveStatus === 'idle' && lastSaved && <span className="text-gray-500 text-[10px] font-bold uppercase tracking-tighter">Changes Saved</span>}
        </div>

        {toasts.map(t => (
          <div
            key={t.id}
            className={`px-6 py-3 rounded-xl shadow-2xl border flex items-center gap-3 animate-in slide-in-from-right-2 fade-in duration-300 ${t.type === 'error' ? 'bg-red-900 border-red-700 text-white' : 'bg-gray-900/90 border-gray-700 text-white backdrop-blur-md'
              }`}
          >
            <span className={t.type === 'error' ? 'text-red-400' : 'text-blue-400'}>●</span>
            <span className="text-sm font-bold tracking-tight">{t.message}</span>
          </div>
        ))}
      </div>
      {
        inspectedRoute && (
          <RoutePreview
            route={inspectedRoute}
            onClose={() => setInspectedRoute(null)}
          />
        )
      }
    </div >
  );
}

export default App;
